/*
  DUK Wunschliste
  ------------------------------------------------------------------------------------------------
  Mehrere Listen, gespeichert im Browser (localStorage „duk:wishlist“, Version 2). Mit Kundenkonto gleicht
  die Liste über den App-Proxy /apps/wunschliste (Cloudflare Worker, Kundenmetafeld custom.wishlist) zwischen
  den Geräten ab. Ist die App nicht installiert oder nicht erreichbar, bleibt alles im Browser.
  Jede Kundin hat fix „Wunschliste“, dazu eigene Listen wie „Geschenk Papa“. Seit DUK 1.3 lädt unter jeder Liste eine
  kleine Box ins Geschäft in 1010 ein (nur dort gibt es das ganze Sortiment); eine eigene Probierliste „Vor Ort
  probieren“ bekommen neue Besucherinnen nicht mehr (Matthias, 06.10.). Wer sie schon hat, behält sie; eine neue Liste
  mit diesem Namen bringt sie zurück.

  Bausteine:
  - <duk-wishlist-button handle="…">: das Herz. Auf Produktkarten setzt dieses Script es selbst ein
    (auch in Algolia-Ergebnisse), auf der Produktseite kommt es aus Liquid.
    Antippen merkt sofort, vorläufig, in der Liste, die während dieses Besuchs zuletzt gewählt wurde (sonst die
    Wunschliste), und öffnet die Auswahl „Deine Listen“. Wählt man dort eine andere Liste, wandert das Produkt dorthin
    statt doppelt zu liegen; erst eine weitere Liste kommt dazu. Auf der Wunschliste-Seite entfernt das Herz direkt
    aus der gezeigten Liste.
  - Auswahl (<dialog>, „Deine Listen“): Listen an/aus, darunter Vorschläge für neue Listen: Anlass der Saison, „Geschenke“, primäre Note
    und Duftfamilie bei Parfums („Feigendüfte“, „Grüne Düfte“), bei Raumdüften „Holzige Raumdüfte“ und
    „Für zuhause“, bei Pflege „Pflege“, bei Home „Für zuhause“.
  - Header: Herz mit Punkt, sobald etwas gemerkt ist ([data-duk-wishlist-dot]).
  - <duk-wishlist-page>: /pages/wunschliste. Seit DUK 1.3 eine Übersicht mit einer Kachel je Liste (seit 1.2.4 ab zwei
    Listen, auch leeren, sonst gleich die Liste) und die geöffnete Liste mit Teilen, Umbenennen, Löschen, Geschäft.
    Geteilte Listen kommen als Link ?w=handle1,handle2&n=Name&s=geschaeft.
  - Klaviyo: Ereignis „Added to Wishlist“ (mit Listenname) für Profile, die Klaviyo schon kennt. Nur mit
    Marketing-Einwilligung (Shopify Customer Privacy API, marketingAllowed()); ohne Einwilligung wird nichts gesendet.
  - Zähler (seit DUK 1.2.3): jedes Merken geht anonym an /apps/wunschliste/count, nur Produkt-ID und Listentyp
    (Wunschliste, Probierliste, eigene Liste; „share“ beim Übernehmen einer geteilten Liste). Kein Listenname, kein
    Cookie. Der Worker zählt pro Tag und Produkt im Shop-Metafeld custom.wishlist_stats und verwirft die Kundennummer.
  - window.DukWishlist: kleine API für Konsole und Erweiterungen.
*/

const STORAGE_KEY = 'duk:wishlist';
const SYNC_SESSION_KEY = 'duk:wishlist:sync';
const LAST_SESSION_KEY = 'duk:wishlist:last';
const CHANGE_EVENT = 'duk-wishlist:change';
const STATUS_EVENT = 'duk-wishlist:status';
const CARD_SECTION = 'duk-wishlist-card';
const SHARE_PARAM = 'w';
const SHARE_NAME_PARAM = 'n';
const SHARE_STORE_PARAM = 's';
const LIST_PARAM = 'liste';
const MAIN = 'main';
const TRY = 'try';
const LIMITS = { lists: 20, items: 100, total: 300, name: 40 };
const MAX_SHARED = 40;
const CONCURRENCY = 6;
const PUSH_DELAY = 700;
const KLAVIYO_EVENT = 'Added to Wishlist';
const COUNT_DELAY = 1500;
const COUNT_MAX = 50;

const CARD_SELECTOR = 'product-card[handle]';
const SKIP_SELECTOR = 'cart-drawer, #search-drawer, predictive-search, [data-duk-wishlist-skip]';

const STRINGS = {
  de: {
    add: 'Auf die Wunschliste',
    remove: 'Aus der Liste entfernen',
    edit: 'Listen bearbeiten',
    saved: 'Auf der Wunschliste',
    savedIn: 'In „{list}“ gemerkt',
    savedInMany: 'In {n} Listen gemerkt',
    mainList: 'Wunschliste',
    tryList: 'Vor Ort probieren',
    unnamed: 'Liste',
    // Regel (1.2.4): die Wunschliste ist ein Ort („auf der Wunschliste“), eigene Listen haben einen Namen („in „Geschenke““)
    addedToast: 'In „{list}“ gemerkt.',
    addedToastMain: 'Auf der Wunschliste gemerkt.',
    addedManyToast: '{n} Produkte in „{list}“ gemerkt.',
    addedManyToastMain: '{n} Produkte auf der Wunschliste gemerkt.',
    savedToast: 'In „{list}“ gemerkt.',
    savedToastMain: 'Auf der Wunschliste gemerkt.',
    savedToastMany: 'In {n} Listen gemerkt.',
    removedToast: 'Aus „{list}“ entfernt.',
    removedToastMain: 'Von der Wunschliste entfernt.',
    removedToastMany: 'Aus {n} Listen entfernt.',
    deletedToast: 'Liste „{list}“ gelöscht.',
    full: 'Die Liste ist voll.',
    fullLists: 'Mehr als 20 Listen gehen nicht.',
    undo: 'Rückgängig',
    view: 'Ansehen',
    changeList: 'Liste ändern',
    pickList: 'Liste wählen',
    viewWishlist: 'Wunschliste ansehen',
    countOne: '1 Produkt',
    countMany: '{n} Produkte',
    copied: 'Link kopiert.',
    shareTitle: '{list} bei Duft und Kultur',
    error: 'Die Wunschliste konnte gerade nicht geladen werden. Bitte die Seite neu laden.',
    pickerTitle: 'Meine Listen',
    addTitle: '{title} hinzufügen',
    newList: 'Neue Liste',
    customList: 'Name der Liste',
    ownName: 'Eigener Name',
    create: 'Anlegen',
    done: 'Fertig',
    addAll: 'Alle in den Warenkorb',
    addingAll: 'Wird hinzugefügt …',
    addedAllOne: 'Ein Produkt in den Warenkorb gelegt.',
    addedAllMany: '{n} Produkte in den Warenkorb gelegt.',
    addedAllSkipped: '{n} nicht verfügbar oder schon im Warenkorb.',
    addAllNone: 'Alles schon im Warenkorb oder gerade nicht verfügbar.',
    addAllError: 'Das hat nicht geklappt. Bitte noch einmal versuchen.',
    close: 'Schließen',
    gifts: 'Geschenke',
    home: 'Für zuhause',
    care: 'Pflege',
    // Scent family for home fragrance: „Holzige Düfte“ becomes „Holzige Raumdüfte“
    homeFamily: { from: 'Düfte', to: 'Raumdüfte' },
    families: {
      ambree: 'Ambrée Düfte', aquatisch: 'Aquatische Düfte', aromatisch: 'Aromatische Düfte', chypre: 'Chypre Düfte',
      floral: 'Florale Düfte', fougere: 'Fougère Düfte', fruchtig: 'Fruchtige Düfte', gourmand: 'Gourmand Düfte',
      gruen: 'Grüne Düfte', holzig: 'Holzige Düfte', ledrig: 'Ledrige Düfte', pudrig: 'Pudrige Düfte',
      rauchig: 'Rauchige Düfte', wuerzig: 'Würzige Düfte', zitrisch: 'Zitrische Düfte'
    },
    seasons: { valentine: 'Valentinstag', mother: 'Muttertag', father: 'Vatertag', christmas: 'Weihnachten' },
    chooseStore: 'Geschäft wählen',
    changeStore: 'Geschäft ändern',
    share: 'Teilen',
    rename: 'Umbenennen',
    delete: 'Löschen',
    save: 'Speichern',
    cancel: 'Abbrechen',
    deleteConfirm: '„{list}“ ({count}) löschen?',
    deleteConfirmEmpty: '„{list}“ löschen?',
    emptyMain: 'Wunschliste ist leer',
    emptyList: 'Liste ist leer',
    emptyTry: 'Liste ist leer',
    emptyHint: 'Tipp bei einem Produkt aufs Herz, dann findest du es hier wieder.\nFür Geschenke und Anlässe lassen sich eigene Listen anlegen und auch per Link teilen.',
    emptyListHint: '',
    emptyTryHint: '',
    storeTitle: 'Besuch uns im Geschäft',
    storeText: 'Lieber vorher probieren? In unserem Geschäft gibt es das ganze Sortiment aus dem Online Store. Zeig uns die Liste, wir beraten gern.',
    // Geteilte Wunschliste ohne Namen: Überschrift „Wunschliste“, darüber klein „Geteilte Liste“ (1.2.4)
    sharedTitle: 'Wunschliste',
    takeOver: 'Als eigene Liste speichern',
    takeOverMain: 'Auf meine Wunschliste',
    ownList: 'Zu meiner Wunschliste',
    allLists: 'Alle Listen',
    listsOne: '1 Liste',
    listsMany: '{n} Listen',
    emptyMeta: 'Noch leer',
    toolCart: 'Alle aus „{list}“ in den Warenkorb',
    toolShare: '„{list}“ teilen',
    toolRename: '„{list}“ umbenennen',
    toolDelete: '„{list}“ löschen',
    newListHint: 'Für Geschenke, Anlässe, Ideen',
    openList: '{list} öffnen',
    noteLocal: 'Gespeichert in diesem Browser.',
    noteLogin: 'Mit Kundenkonto auf allen Geräten.',
    login: 'Anmelden',
    noteAccount: 'Im Kundenkonto gespeichert, auf allen Geräten.',
    noteBusy: 'Wird im Kundenkonto gespeichert …',
    noteError: 'Kundenkonto gerade nicht erreichbar. Gespeichert in diesem Browser.'
  },
  en: {
    add: 'Add to wishlist',
    remove: 'Remove from list',
    edit: 'Edit lists',
    saved: 'On your wishlist',
    savedIn: 'Saved to “{list}”',
    savedInMany: 'Saved to {n} lists',
    mainList: 'Wishlist',
    tryList: 'Try in store',
    unnamed: 'List',
    addedToast: 'Saved to “{list}”.',
    addedToastMain: 'Saved to your wishlist.',
    addedManyToast: '{n} items saved to “{list}”.',
    addedManyToastMain: '{n} items saved to your wishlist.',
    savedToast: 'Saved to “{list}”.',
    savedToastMain: 'Saved to your wishlist.',
    savedToastMany: 'Saved to {n} lists.',
    removedToast: 'Removed from “{list}”.',
    removedToastMain: 'Removed from your wishlist.',
    removedToastMany: 'Removed from {n} lists.',
    deletedToast: 'List “{list}” deleted.',
    full: 'This list is full.',
    fullLists: 'You can have up to 20 lists.',
    undo: 'Undo',
    view: 'View',
    changeList: 'Change list',
    pickList: 'Choose list',
    viewWishlist: 'View wishlist',
    countOne: '1 item',
    countMany: '{n} items',
    copied: 'Link copied.',
    shareTitle: '{list} at Duft und Kultur',
    error: 'The wishlist could not be loaded. Please reload the page.',
    pickerTitle: 'My lists',
    addTitle: 'Add {title}',
    newList: 'New list',
    customList: 'List name',
    ownName: 'Custom name',
    create: 'Create',
    done: 'Done',
    addAll: 'Add all to cart',
    addingAll: 'Adding …',
    addedAllOne: 'One item added to your cart.',
    addedAllMany: '{n} items added to your cart.',
    addedAllSkipped: '{n} unavailable or already in your cart.',
    addAllNone: 'Everything is already in your cart or currently unavailable.',
    addAllError: 'That didn’t work. Please try again.',
    close: 'Close',
    gifts: 'Gifts',
    home: 'For the home',
    care: 'Body Care',
    homeFamily: { from: 'Fragrances', to: 'Home Fragrances' },
    families: {
      ambree: 'Amber Fragrances', aquatisch: 'Aquatic Fragrances', aromatisch: 'Aromatic Fragrances', chypre: 'Chypre Fragrances',
      floral: 'Floral Fragrances', fougere: 'Fougère Fragrances', fruchtig: 'Fruity Fragrances', gourmand: 'Gourmand Fragrances',
      gruen: 'Green Fragrances', holzig: 'Woody Fragrances', ledrig: 'Leather Fragrances', pudrig: 'Powdery Fragrances',
      rauchig: 'Smoky Fragrances', wuerzig: 'Spicy Fragrances', zitrisch: 'Citrus Fragrances'
    },
    seasons: { valentine: 'Valentine’s Day', mother: 'Mother’s Day', father: 'Father’s Day', christmas: 'Christmas' },
    chooseStore: 'Choose store',
    changeStore: 'Change store',
    share: 'Share',
    rename: 'Rename',
    delete: 'Delete',
    save: 'Save',
    cancel: 'Cancel',
    deleteConfirm: 'Delete “{list}” with {count}?',
    deleteConfirmEmpty: 'Delete “{list}”?',
    emptyMain: 'Your wishlist is empty',
    emptyList: 'This list is empty',
    emptyTry: 'This list is empty',
    emptyHint: 'Tap the heart on any product to find it here.\nCreate lists for gifts and occasions, and share them with a link if you like.',
    emptyListHint: '',
    emptyTryHint: '',
    storeTitle: 'Visit our shop',
    storeText: 'Want to try first? Our shop carries the full online range. Show us your list and we’re happy to help.',
    sharedTitle: 'Wishlist',
    takeOver: 'Save as my own list',
    takeOverMain: 'Save to my wishlist',
    ownList: 'Go to my wishlist',
    allLists: 'All lists',
    listsOne: '1 list',
    listsMany: '{n} lists',
    emptyMeta: 'Empty',
    toolCart: 'Add all from “{list}” to cart',
    toolShare: 'Share “{list}”',
    toolRename: 'Rename “{list}”',
    toolDelete: 'Delete “{list}”',
    newListHint: 'For gifts, occasions, ideas',
    openList: 'Open {list}',
    noteLocal: 'Saved in this browser.',
    noteLogin: 'With a customer account on all devices.',
    login: 'Sign in',
    noteAccount: 'Saved in your customer account, on all devices.',
    noteBusy: 'Saving to your customer account …',
    noteError: 'Customer account not reachable right now. Saved in this browser.'
  }
};

// Same outline as the theme's heart pictogram (snippets/icon.liquid, picto-heart), drawn on a 24 grid
const HEART_PATH = 'M10.6569 4.6569L11.9999 6L13.3432 4.65678C14.404 3.59596 15.8428 3 17.3431 3C20.4673 3 22.9999 5.53273 22.9999 8.65694C22.9999 10.1572 22.4039 11.596 21.3431 12.6568L11.9999 22L2.65684 12.6569C1.59598 11.596 1 10.1571 1 8.65685C1 5.53267 3.53265 3 6.65682 3C8.15713 3 9.59599 3.59601 10.6569 4.6569Z';

/* ---------------------------------------------------------------------------------------------- */
/* Configuration and helpers                                                                       */
/* ---------------------------------------------------------------------------------------------- */

function getConfig() {
  const element = document.getElementById('duk-wishlist-config');
  let raw = {};

  try {
    raw = element ? JSON.parse(element.textContent) || {} : {};
  } catch (error) {
    raw = {};
  }

  const stores = (Array.isArray(raw.stores) ? raw.stores : [])
    .filter((store) => store && typeof store.id === 'string' && store.id)
    .map((store) => ({
      id: store.id,
      name: String(store.name || ''),
      place: String(store.place || store.name || ''),
      address: String(store.address || ''),
      hours: String(store.hours || ''),
      zip: String(store.zip || ''),
      lat: Number(store.lat) || null,
      lng: Number(store.lng) || null,
      route: typeof store.route === 'string' && store.route.startsWith('https://') ? store.route : '',
      url: typeof store.url === 'string' ? store.url : ''
    }))
    .sort((a, b) => a.zip.localeCompare(b.zip) || a.place.localeCompare(b.place));

  return {
    locale: raw.locale === 'de' ? 'de' : 'en',
    pageUrl: typeof raw.pageUrl === 'string' && raw.pageUrl ? raw.pageUrl : '/pages/wunschliste',
    strokeWidth: Number(raw.strokeWidth) || 2.1,
    customer: raw.customer ? String(raw.customer) : null,
    accountUpdated: Number(raw.accountUpdated) || 0,
    syncUrl: typeof raw.syncUrl === 'string' ? raw.syncUrl.replace(/\/+$/, '') : '',
    loginUrl: typeof raw.loginUrl === 'string' ? raw.loginUrl : '',
    scheme: typeof raw.scheme === 'string' && /^[\w-]+$/.test(raw.scheme) ? raw.scheme : 'scheme-1',
    stores
  };
}

const config = getConfig();

// Lists made from a suggestion keep the name they were created with. On the site in the other language they read in
// that language: „Pflege“ shows as „Body Care“ (Matthias, 04.10.). Names the customer typed stay as they are.
const labelCache = {};

function suggestedLabels(locale) {
  if (labelCache[locale]) return labelCache[locale];

  const strings = STRINGS[locale];
  const labels = { gifts: strings.gifts, home: strings.home, care: strings.care };

  for (const [key, value] of Object.entries(strings.seasons)) labels[`season-${key}`] = value;

  for (const [key, value] of Object.entries(strings.families)) {
    labels[`family-${key}`] = value;
    labels[`home-${key}`] = value.replace(strings.homeFamily.from, strings.homeFamily.to);
  }

  labelCache[locale] = labels;
  return labels;
}

// Ein Vorschlagsname in irgendeiner Sprache steht für dieselbe Liste: „Pflege“ (.at) und „Body Care“ (.com) werden beim
// Zusammenführen eins (1.2.4, Matthias 06.10.: zwei Listen „Pflege“ nach dem Länderwechsel)
function nameKey(name) {
  const wanted = String(name || '').trim().toLowerCase();
  if (!wanted) return '';

  for (const locale of Object.keys(STRINGS)) {
    const labels = suggestedLabels(locale);
    const key = Object.keys(labels).find((entry) => labels[entry].toLowerCase() === wanted);
    if (key) return `idea:${key}`;
  }

  return wanted;
}

function localName(name) {
  const wanted = String(name).trim().toLowerCase();

  for (const locale of Object.keys(STRINGS)) {
    if (locale === config.locale) continue;

    const labels = suggestedLabels(locale);
    const key = Object.keys(labels).find((entry) => labels[entry].toLowerCase() === wanted);

    if (key) return suggestedLabels(config.locale)[key] || name;
  }

  return name;
}

function t(key, vars = {}) {
  let text = STRINGS[config.locale][key] ?? STRINGS.de[key] ?? key;

  if (typeof text !== 'string') return text;

  for (const [name, value] of Object.entries(vars)) {
    text = text.split(`{${name}}`).join(String(value));
  }

  return text;
}

const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]));

const routesRoot = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

// Shopify handles never contain whitespace, slashes or URL delimiters
const validHandle = (handle) => typeof handle === 'string' && handle.length > 0 && handle.length <= 255 && /^[^\s/?#&,]+$/.test(handle);

// A value inside a CSS attribute selector
const cssValue = (value) => (window.CSS && typeof window.CSS.escape === 'function' ? window.CSS.escape(value) : value.replace(/["\\]/g, '\\$&'));

const heartSvg = () => `<svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" overflow="visible" class="duk-wishlist-heart"><path d="${HEART_PATH}" stroke="currentColor" stroke-width="${config.strokeWidth}"/></svg>`;

const checkSvg = '<svg aria-hidden="true" focusable="false" viewBox="0 0 16 16" class="duk-wl-check"><path d="M3 8.5l3.2 3L13 4.5" fill="none" stroke="currentColor" stroke-width="1.6"/></svg>';
const closeSvg = '<svg aria-hidden="true" focusable="false" viewBox="0 0 16 16" width="16" height="16"><path d="M3 3l10 10M13 3L3 13" fill="none" stroke="currentColor" stroke-width="1.4"/></svg>';

function toast(message, duration = 3000) {
  document.dispatchEvent(new CustomEvent('toast:show', { detail: { message, duration } }));
}

const countLabel = (count) => (count === 1 ? t('countOne') : t('countMany', { n: count }));

// Leere Liste: ein dezentes Herz statt „&“ (Matthias, 06.10.), dasselbe Zeichen, mit dem man die Liste füllt
const emptyHeart = () => `<svg class="duk-wl-empty-heart" aria-hidden="true" focusable="false" viewBox="0 0 24 24" overflow="visible"><path d="${HEART_PATH}" fill="none" stroke="currentColor" stroke-width="1"/></svg>`;
// Symbole unter jeder Kachel der Übersicht (Matthias, 06.10.: statt „···“-Menü), Strich wie das Teilen-Symbol
const toolSvg = (path) => `<svg aria-hidden="true" focusable="false" width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round">${path}</svg>`;
const CART_TOOL = toolSvg('<path d="M3.2 5.5h9.6l-.9 8.5H4.1z"/><path d="M5.7 5.5V4.4a2.3 2.3 0 0 1 4.6 0v1.1"/>');
const SHARE_TOOL = toolSvg('<path d="M8 10.5V1.5M4.8 4.6 8 1.5l3.2 3.1M3 7.5v6.5h10V7.5"/>');
const RENAME_TOOL = toolSvg('<path d="M10.9 2.1l3 3-8.6 8.6-3.8.8.8-3.8z"/><path d="M9.4 3.6l3 3"/>');
const STORE_TOOL = toolSvg('<path d="M8 14.5s4.5-4.3 4.5-8a4.5 4.5 0 0 0-9 0c0 3.7 4.5 8 4.5 8z"/><circle cx="8" cy="6.5" r="1.6"/>');
const DELETE_TOOL = toolSvg('<path d="M2.5 4h11M6.2 4V2.4h3.6V4M3.9 4l.8 9.6h6.6l.8-9.6M6.6 6.6v4.6M9.4 6.6v4.6"/>');

const storeById = (id) => config.stores.find((store) => store.id === id) || null;

function sessionGet(key) {
  try {
    return window.sessionStorage.getItem(key);
  } catch (error) {
    return null;
  }
}

function sessionSet(key, value) {
  try {
    window.sessionStorage.setItem(key, value);
  } catch (error) {
    /* private mode */
  }
}

// DUK 1.3: links to a list always carry it, also the Wunschliste. The page without ?liste= decides by itself
// (1.2.4: overview from two lists on, also empty ones, see autoView); ?liste=alle is the overview.
const OVERVIEW = 'alle';

function listUrl(listId) {
  return `${config.pageUrl}?${LIST_PARAM}=${encodeURIComponent(listId || MAIN)}`;
}

// Wunschliste and Probierliste are there from the start. Only the Wunschliste cannot be deleted; the Probierliste
// can (Matthias, 05.10.) and then stays away until a list with its name is created again.
const isFixed = (id) => id === MAIN || id === TRY;

// Names that mean the Probierliste (both languages, plus the names used before: shared links keep working)
const TRY_NAMES = [STRINGS.de.tryList, STRINGS.en.tryList, 'Probierliste', 'Vor Ort probieren'].map((name) => name.toLowerCase());
const isTryName = (name) => typeof name === 'string' && TRY_NAMES.includes(name.trim().toLowerCase());

// Product groups (custom.product_group: Parfum, Raumduft, Pflege, Home) decide the ideas (Matthias, 04.10.):
// perfume → primary note and scent family; home fragrance → „Holzige Raumdüfte“ and „Für zuhause“;
// body care → „Pflege“; home → „Für zuhause“. „Für zuhause“ also when no product is in view.
const PERFUME_GROUP = 'Parfum';
const HOME_FRAGRANCE_GROUP = 'Raumduft';
const CARE_GROUP = 'Pflege';
const HOME_GROUPS = ['Raumduft', 'Home'];

// Inspiration for new lists: the occasion in season first (Austrian dates)
const SEASONS = [
  { key: 'valentine', from: (year) => new Date(year, 0, 20), to: (year) => new Date(year, 1, 14) },
  { key: 'mother', from: (year) => addDays(secondSunday(year, 4), -28), to: (year) => secondSunday(year, 4) },
  { key: 'father', from: (year) => addDays(secondSunday(year, 5), -28), to: (year) => secondSunday(year, 5) },
  { key: 'christmas', from: (year) => new Date(year, 9, 15), to: (year) => new Date(year, 11, 24) }
];

function secondSunday(year, month) {
  const first = new Date(year, month, 1);
  return new Date(year, month, 1 + ((7 - first.getDay()) % 7) + 7);
}

function addDays(date, days) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

function seasonFor(date = new Date()) {
  const day = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const season = SEASONS.find((entry) => day >= entry.from(day.getFullYear()) && day <= entry.to(day.getFullYear()));
  return season ? t('seasons')[season.key] : '';
}

/* ---------------------------------------------------------------------------------------------- */
/* Data model (kept identical to the copy in the Cloudflare Worker)                                */
/* ---------------------------------------------------------------------------------------------- */

const cleanName = (value) => (typeof value === 'string' ? value.replace(/[\x00-\x1f\x7f]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, LIMITS.name) : '');
const cleanId = (value) => (typeof value === 'string' && /^[a-z0-9-]{1,24}$/.test(value) ? value : '');
const cleanStore = (value) => (typeof value === 'string' && /^[a-z0-9-]{1,64}$/.test(value) ? value : '');

function normalizeItems(items) {
  const seen = new Set();
  const result = [];

  for (const item of Array.isArray(items) ? items : []) {
    const handle = item && typeof item.handle === 'string' ? item.handle.trim() : '';

    if (!validHandle(handle) || seen.has(handle)) continue;

    seen.add(handle);
    result.push({ handle, id: Number(item.id) || null, added: Number(item.added) || 0 });
  }

  return result;
}

// Main list first and always present; unique ids; caps on lists, items per list and items overall
function normalizeLists(input) {
  const source = Array.isArray(input) ? input.filter((list) => list && typeof list === 'object') : [];
  const ordered = [...source.filter((list) => list.id === MAIN), ...source.filter((list) => list.id !== MAIN)];
  const lists = [];
  const ids = new Set();
  let total = 0;

  for (const raw of ordered) {
    const id = cleanId(raw.id);

    if (!id || ids.has(id)) continue;
    if (lists.length >= LIMITS.lists) break;

    const items = normalizeItems(raw.items).slice(0, Math.min(LIMITS.items, Math.max(0, LIMITS.total - total)));
    total += items.length;
    ids.add(id);
    lists.push({ id, name: cleanName(raw.name), store: cleanStore(raw.store), created: Number(raw.created) || 0, items });
  }

  if (!ids.has(MAIN)) {
    lists.unshift({ id: MAIN, name: '', store: '', created: 0, items: [] });
    if (lists.length > LIMITS.lists) lists.length = LIMITS.lists;
  }

  return lists;
}

// Union of two states: same id, or same name and store, counts as the same list. Nothing gets deleted.
function mergeLists(base, other) {
  const result = normalizeLists(base).map((list) => ({ ...list, items: list.items.slice() }));
  const sameName = (a, b) => a.id !== MAIN && b.id !== MAIN && a.name !== '' && nameKey(a.name) === nameKey(b.name) && a.store === b.store;

  for (const list of normalizeLists(other)) {
    const target = result.find((entry) => entry.id === list.id) || result.find((entry) => sameName(entry, list));

    if (!target) {
      result.push({ ...list, items: list.items.slice() });
      continue;
    }

    if (!target.name && list.name) target.name = list.name;
    if (!target.store && list.store) target.store = list.store;

    for (const item of list.items) {
      const existing = target.items.find((entry) => entry.handle === item.handle);

      if (!existing) {
        target.items.push({ ...item });
      } else if (!existing.id && item.id) {
        existing.id = item.id;
      }
    }

    target.items.sort((a, b) => b.added - a.added);
  }

  return normalizeLists(result);
}

// Gelöschtes als Zeitstempel (1.2.5, Fehlerliste Nr. 6): Beim Zusammenführen zweier Stände bleibt ein Produkt oder eine
// Liste weg, wenn es nach dem Hinzufügen gelöscht wurde. Schlüssel „l:<liste>“ und „i:<liste>:<handle>“, 120 Tage.
// Dieselbe Regel steht im Worker.
const REMOVED_DAYS = 120;
const REMOVED_MAX = 500;
const REMOVED_KEY = /^(l:[a-z0-9-]{1,24}|i:[a-z0-9-]{1,24}:[^\s/?#&,]{1,255})$/;

function normalizeRemoved(input, now = Date.now()) {
  const result = {};
  if (!input || typeof input !== 'object' || Array.isArray(input)) return result;

  const cutoff = now - REMOVED_DAYS * 864e5;
  Object.entries(input)
    .filter(([key, value]) => REMOVED_KEY.test(key) && Number(value) > cutoff && Number(value) <= now + 864e5)
    .sort((a, b) => Number(b[1]) - Number(a[1]))
    .slice(0, REMOVED_MAX)
    .forEach(([key, value]) => { result[key] = Number(value); });

  return result;
}

function mergeRemoved(a, b) {
  const result = { ...normalizeRemoved(a) };
  for (const [key, value] of Object.entries(normalizeRemoved(b))) {
    if (!result[key] || value > result[key]) result[key] = value;
  }
  return normalizeRemoved(result);
}

function applyRemoved(lists, removed) {
  return normalizeLists(lists
    .filter((list) => list.id === MAIN || !(removed[`l:${list.id}`] > (Number(list.created) || 0)))
    .map((list) => ({ ...list, items: list.items.filter((item) => !(removed[`i:${list.id}:${item.handle}`] > (Number(item.added) || 0))) })));
}

/* ---------------------------------------------------------------------------------------------- */
/* Storage                                                                                         */
/* ---------------------------------------------------------------------------------------------- */

const newTryList = () => ({ id: TRY, name: '', store: '', created: 0, items: [] });

// Only in the browser (the Worker stores whatever lists it gets): Wunschliste first, Probierliste second (if there is one),
// linked to the first store. A list named „Vor Ort probieren“ becomes the Probierliste. Since DUK 1.3 nobody gets an
// empty Probierliste any more (addTry stays false): the invitation to the store sits under every list instead.
function withDefaults(input, addTry = false) {
  const lists = normalizeLists(input);
  let tryList = lists.find((list) => list.id === TRY);

  if (!tryList) {
    tryList = lists.find((list) => list.id !== MAIN && isTryName(list.name)) || null;

    if (tryList) {
      tryList.id = TRY;
      tryList.name = '';
    } else if (addTry) {
      tryList = newTryList();
    }
  }

  if (tryList && !storeById(tryList.store)) tryList.store = config.stores.length > 0 ? config.stores[0].id : '';

  return [lists[0], ...(tryList ? [tryList] : []), ...lists.filter((list) => list.id !== MAIN && list !== tryList)].slice(0, LIMITS.lists);
}

function emptyState() {
  return { v: 2, lists: withDefaults([]), last: MAIN, owner: null, synced: 0, dirty: false, removed: {} };
}

function parseState(data) {
  if (!data || typeof data !== 'object') return emptyState();

  // Version 1: one flat list
  if (data.v !== 2) {
    return { ...emptyState(), lists: withDefaults([{ id: MAIN, items: data.items }]) };
  }

  const lists = withDefaults(data.lists);

  return {
    v: 2,
    lists,
    last: lists.some((list) => list.id === data.last) ? data.last : MAIN,
    owner: data.owner ? String(data.owner) : null,
    synced: Number(data.synced) || 0,
    dirty: Boolean(data.dirty),
    removed: normalizeRemoved(data.removed)
  };
}

// Browser storage can throw (private mode, blocked storage): the lists then live in memory for this page
const localStore = {
  load() {
    try {
      return parseState(JSON.parse(window.localStorage.getItem(STORAGE_KEY) || 'null'));
    } catch (error) {
      return emptyState();
    }
  },

  save(state) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      return true;
    } catch (error) {
      return false;
    }
  }
};

/* ---------------------------------------------------------------------------------------------- */
/* Wishlist state                                                                                  */
/* ---------------------------------------------------------------------------------------------- */

const uid = () => `l-${Date.now().toString(36).slice(-5)}${Math.random().toString(36).slice(2, 7)}`;

const wishlist = {
  state: localStore.load(),
  revision: 0,

  get lists() {
    return this.state.lists;
  },

  get removed() {
    if (!this.state.removed || typeof this.state.removed !== 'object') this.state.removed = {};
    return this.state.removed;
  },

  // Lists that count for the heart and the header heart: an empty Probierliste from DUK 1.2 does not count
  get inUse() {
    return this.state.lists.filter((list) => !(list.id === TRY && list.items.length === 0));
  },

  // The list the heart uses: the one last chosen during this visit, otherwise the Wunschliste (Matthias, 04.10.)
  get lastId() {
    const last = sessionGet(LAST_SESSION_KEY);
    return last && this.list(last) ? last : MAIN;
  },

  remember(id) {
    this.state.last = id;
    sessionSet(LAST_SESSION_KEY, id);
  },

  list(id) {
    return this.state.lists.find((list) => list.id === id) || null;
  },

  name(listOrId) {
    const list = typeof listOrId === 'string' ? this.list(listOrId) : listOrId;
    if (!list) return '';
    if (list.name) return localName(list.name);
    if (list.id === MAIN) return t('mainList');
    return list.id === TRY ? t('tryList') : t('unnamed');
  },

  total() {
    return this.state.lists.reduce((sum, list) => sum + list.items.length, 0);
  },

  // Nothing saved and no own lists (the two fixed lists alone do not count)
  isEmpty() {
    return this.total() === 0 && this.state.lists.every((list) => isFixed(list.id) && !list.name);
  },

  hasMultiple() {
    return this.state.lists.length > 1;
  },

  has(handle) {
    return this.state.lists.some((list) => list.items.some((item) => item.handle === handle));
  },

  inList(listId, handle) {
    const list = this.list(listId);
    return Boolean(list && list.items.some((item) => item.handle === handle));
  },

  listsWith(handle) {
    return this.state.lists.filter((list) => list.items.some((item) => item.handle === handle));
  },

  // Returns the list on success, null when the product is already there or a limit is reached
  add(handle, listId = this.lastId, data = {}) {
    const list = this.list(listId) || this.list(MAIN);

    if (!validHandle(handle) || list.items.some((item) => item.handle === handle)) return null;

    if (list.items.length >= LIMITS.items || this.total() >= LIMITS.total) {
      toast(escapeHtml(t('full')));
      return null;
    }

    // Wieder dazu (auch „Rückgängig“): gilt als neuer als die Löschung, sonst nähme ein Abgleich es wieder heraus
    const key = `i:${list.id}:${handle}`;
    const removedAt = this.removed[key] || 0;
    delete this.removed[key];
    list.items.unshift({ handle, id: Number(data.id) || null, added: Math.max(Number(data.added) || Date.now(), removedAt + 1) });
    this.remember(list.id);
    this.commit({ handle, listId: list.id, added: true });
    return list;
  },

  // Returns the removed entry (for undo)
  remove(handle, listId) {
    const list = this.list(listId);
    if (!list) return null;

    const index = list.items.findIndex((item) => item.handle === handle);
    if (index === -1) return null;

    const [entry] = list.items.splice(index, 1);
    this.removed[`i:${list.id}:${handle}`] = Date.now();
    this.commit({ handle, listId: list.id, added: false });
    return entry;
  },

  createList(name, store = '') {
    if (this.state.lists.length >= LIMITS.lists) {
      toast(escapeHtml(t('fullLists')));
      return null;
    }

    // „Vor Ort probieren“ after the Probierliste was deleted: it comes back as the Probierliste, in second place
    if (isTryName(name) && !this.list(TRY)) {
      delete this.removed[`l:${TRY}`];
      const tryList = { ...newTryList(), store: storeById(store) ? store : config.stores.length > 0 ? config.stores[0].id : '', created: Date.now() };
      this.state.lists.splice(1, 0, tryList);
      this.remember(TRY);
      this.commit({ listId: TRY, created: true });
      return tryList;
    }

    const list = { id: uid(), name: cleanName(name), store: cleanStore(store), created: Date.now(), items: [] };
    this.state.lists.push(list);
    this.remember(list.id);
    this.commit({ listId: list.id, created: true });
    return list;
  },

  renameList(id, name) {
    const list = this.list(id);
    if (!list) return;

    list.name = cleanName(name);
    this.commit({ listId: id, renamed: true });
  },

  setStore(id, store) {
    const list = this.list(id);
    if (!list) return;

    list.store = cleanStore(store);
    this.commit({ listId: id, store: true });
  },

  // Returns the deleted list and its place (for undo)
  deleteList(id) {
    const index = this.state.lists.findIndex((list) => list.id === id);
    if (id === MAIN || index === -1) return null;

    const [list] = this.state.lists.splice(index, 1);
    if (this.state.last === id) this.state.last = MAIN;
    this.removed[`l:${id}`] = Date.now();
    this.commit({ listId: id, deleted: true });
    return { list, index };
  },

  // Undo of a deletion: the list comes back at its place, as far as the limits allow
  restoreList(list, index) {
    if (!list || this.list(list.id)) return null;

    if (this.state.lists.length >= LIMITS.lists) {
      toast(escapeHtml(t('fullLists')));
      return null;
    }

    const room = Math.max(0, LIMITS.total - this.total());
    const removedAt = this.removed[`l:${list.id}`] || 0;
    delete this.removed[`l:${list.id}`];
    const restored = { ...list, created: Math.max(Number(list.created) || 0, removedAt + 1), items: list.items.slice(0, Math.min(LIMITS.items, room)) };
    const first = this.list(TRY) ? 2 : 1;
    this.state.lists.splice(restored.id === TRY ? 1 : Math.max(first, Math.min(index, this.state.lists.length)), 0, restored);
    this.commit({ listId: restored.id, restored: true });
    return restored;
  },

  // Quiet updates (no event, no sync push needed right away): product ID, renamed handle
  update(handle, changes) {
    let touched = false;

    for (const list of this.state.lists) {
      const item = list.items.find((entry) => entry.handle === handle);
      if (!item) continue;

      if (changes.id && !item.id) {
        item.id = Number(changes.id) || null;
        touched = true;
      }

      if (changes.handle && validHandle(changes.handle) && changes.handle !== handle) {
        if (list.items.some((entry) => entry.handle === changes.handle)) {
          list.items = list.items.filter((entry) => entry !== item);
        } else {
          item.handle = changes.handle;
        }

        touched = true;
      }
    }

    if (touched) {
      this.markDirty();
      localStore.save(this.state);
      sync.schedule();
    }
  },

  markDirty() {
    this.revision += 1;
    if (config.customer && sync.possible) {
      this.state.owner = config.customer;
      this.state.dirty = true;
    }
  },

  commit(detail) {
    this.markDirty();
    localStore.save(this.state);
    emit(detail);
    sync.schedule();
  },

  // The account copy wins (after the server merged it). options.fresh: nothing saved in the account yet
  adopt(remote, options = {}) {
    const last = this.state.last;
    const removed = mergeRemoved(this.state.removed, remote.removed);
    this.state = { v: 2, lists: withDefaults(remote.lists), last, owner: config.customer, synced: Number(remote.updated) || 0, dirty: Boolean(options.dirty), removed };
    if (!this.list(last)) this.state.last = MAIN;
    localStore.save(this.state);
    emit({ external: true });
  },

  // Signed out: the account copy does not stay on this device
  reset() {
    this.state = emptyState();
    localStore.save(this.state);
    emit({ external: true });
  },

  // Another tab saved: counts as a change, so an answer to a save that was already on its way does not overwrite it
  // (1.2.5, Fehlerliste Nr. 6)
  reload() {
    this.state = localStore.load();
    this.revision += 1;
    emit({ external: true });
  }
};

function emit(detail = {}) {
  document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail }));
}

// Another tab changed the lists
window.addEventListener('storage', (event) => {
  if (event.key === STORAGE_KEY || event.key === null) wishlist.reload();
});

/* ---------------------------------------------------------------------------------------------- */
/* Account sync over the app proxy                                                                 */
/* ---------------------------------------------------------------------------------------------- */

const sync = {
  status: 'off',
  busy: false,
  again: false,
  timer: 0,

  get possible() {
    return Boolean(config.syncUrl) && sessionGet(SYNC_SESSION_KEY) !== 'off';
  },

  setStatus(status) {
    if (this.status === status) return;
    this.status = status;
    document.dispatchEvent(new CustomEvent(STATUS_EVENT, { detail: { status } }));
  },

  disable() {
    sessionSet(SYNC_SESSION_KEY, 'off');
    window.clearTimeout(this.timer);

    // Lists that never reached the account stay plain browser lists
    const state = wishlist.state;
    if (state.owner && !state.synced) {
      state.owner = null;
      state.dirty = false;
      localStore.save(state);
    }

    this.setStatus('off');
  },

  async request(method, body) {
    let response;

    try {
      response = await fetch(`${config.syncUrl}/state`, {
        method,
        credentials: 'same-origin',
        cache: 'no-store',
        headers: body ? { Accept: 'application/json', 'Content-Type': 'application/json' } : { Accept: 'application/json' },
        body: body ? JSON.stringify(body) : undefined
      });
    } catch (error) {
      this.setStatus('error');
      return null;
    }

    // No app behind the proxy, not signed in there, or a storefront page instead of JSON: stay local for this visit
    if ([401, 403, 404].includes(response.status)) {
      this.disable();
      return null;
    }

    let data = null;

    try {
      data = await response.json();
    } catch (error) {
      // An error page (5xx, Cloudflare) is a hiccup, not a missing app: keep the changes and try again later
      // (1.2.5, Fehlerliste Nr. 6). Only a normal page instead of JSON means there is no app.
      if (response.status >= 500) {
        this.setStatus('error');
      } else {
        this.disable();
      }
      return null;
    }

    if (!response.ok || !data || !Array.isArray(data.lists) || String(data.customer) !== config.customer) {
      this.setStatus('error');
      return null;
    }

    sessionSet(SYNC_SESSION_KEY, 'on');
    return { lists: normalizeLists(data.lists), updated: Number(data.updated) || 0, removed: normalizeRemoved(data.removed) };
  },

  async init() {
    const state = wishlist.state;

    if (!config.customer) {
      // Signed out: an account copy does not stay on this device, never-synced lists stay as they are
      if (state.owner && state.synced) {
        wishlist.reset();
      } else if (state.owner) {
        state.owner = null;
        state.dirty = false;
        localStore.save(state);
      }

      this.setStatus('off');
      return;
    }

    if (!this.possible) {
      this.setStatus('off');
      return;
    }

    const own = state.owner === config.customer;
    const otherAccount = Boolean(state.owner) && !own;

    if (own && state.dirty) {
      await this.push();
      return;
    }

    if (own && config.accountUpdated <= state.synced) {
      this.setStatus('ready');
      return;
    }

    const revision = wishlist.revision;
    this.setStatus('busy');
    const remote = await this.request('GET');
    if (!remote) return;

    const changed = wishlist.revision !== revision;

    // Another account's copy on this device, an older own copy, or nothing saved yet: take the account's lists
    if (otherAccount || (!changed && (own || wishlist.isEmpty()))) {
      wishlist.adopt(remote, { fresh: !remote.updated });
      this.setStatus('ready');
      return;
    }

    // Lists saved before signing in (or changed while loading) move into the account
    const removed = mergeRemoved(wishlist.removed, remote.removed);
    wishlist.adopt({ lists: applyRemoved(mergeLists(remote.lists, wishlist.state.lists), removed), updated: remote.updated, removed }, { dirty: true });
    await this.push();
  },

  schedule() {
    if (!config.customer || !this.possible) return;

    window.clearTimeout(this.timer);
    this.timer = window.setTimeout(() => this.push(), PUSH_DELAY);
  },

  async push() {
    if (this.busy) {
      this.again = true;
      return;
    }

    this.busy = true;
    this.again = false;
    this.setStatus('busy');

    const revision = wishlist.revision;
    const remote = await this.request('POST', { base: wishlist.state.synced || 0, lists: wishlist.state.lists, removed: wishlist.removed });

    this.busy = false;

    if (remote) {
      this.retries = 0;

      if (wishlist.revision === revision) {
        wishlist.adopt(remote);
      } else {
        // Changed again while saving (here or in another tab): keep what the server merged in from other devices and
        // the newer local changes, deletions included, and save that next (1.2.5, Fehlerliste Nr. 6)
        const removed = mergeRemoved(wishlist.removed, remote.removed);
        wishlist.state.lists = withDefaults(applyRemoved(mergeLists(remote.lists, wishlist.state.lists), removed));
        wishlist.state.removed = removed;
        wishlist.state.synced = remote.updated;
        localStore.save(wishlist.state);
        emit({ external: true });
        this.again = true;
      }

      this.setStatus('ready');
    } else if (this.status === 'error' && wishlist.state.dirty && (this.retries || 0) < 6) {
      // Server hiccup: try again in 5 s, 10 s, 20 s … up to a minute; on the next visit the dirty state is saved anyway
      this.retries = (this.retries || 0) + 1;
      window.clearTimeout(this.timer);
      this.timer = window.setTimeout(() => this.push(), Math.min(60000, 5000 * 2 ** (this.retries - 1)));
      return;
    }

    if (this.again) this.schedule();
  },

  // Guests on the wishlist page: is account sync set up at all? (once per visit)
  async probe() {
    const known = sessionGet(SYNC_SESSION_KEY);
    if (!config.syncUrl || known === 'off') return false;
    if (known === 'on') return true;

    try {
      const response = await fetch(`${config.syncUrl}/ping`, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } });
      const data = response.ok ? await response.json() : null;
      const available = Boolean(data && data.ok);
      sessionSet(SYNC_SESSION_KEY, available ? 'on' : 'off');
      return available;
    } catch (error) {
      return false;
    }
  }
};

/* ---------------------------------------------------------------------------------------------- */
/* Länderwechsel: die Listen reisen mit (Matthias, 06.10.)                                         */
/* ---------------------------------------------------------------------------------------------- */

// Österreich liegt auf .at, Deutschland auf .com, und der Browser speichert je Domain. Beim Wechsel des Landes hängen
// die Listen an der Rücksprungadresse des Länderformulars (?wl=…); drüben werden sie mit den dortigen zusammengeführt
// und die Adresse gleich wieder sauber gemacht. Den Warenkorb nimmt assets/duk-cart.js mit.
const CARRY_PARAM = 'wl';
const CARRY_MAX_ITEMS = 60;
// Gelöschtes reist mit (Matthias, 08.10.: nach dem Länderwechsel waren gelöschte Listen wieder da). Nur die jüngsten,
// damit die Adresse kurz bleibt; drüben wirkt dieselbe Regel wie beim Konto-Abgleich (applyRemoved).
const CARRY_MAX_REMOVED = 20;
const CARRY_REMOVED_DAYS = 30;

function toBase64Url(text) {
  let binary = '';
  new TextEncoder().encode(text).forEach((byte) => { binary += String.fromCharCode(byte); });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function fromBase64Url(value) {
  const binary = atob(value.replace(/-/g, '+').replace(/_/g, '/'));
  return new TextDecoder().decode(Uint8Array.from(binary, (char) => char.charCodeAt(0)));
}

function carryValue() {
  let room = CARRY_MAX_ITEMS;
  const lists = wishlist.lists
    .filter((list) => list.items.length > 0 || list.name)
    .map((list) => {
      const handles = list.items.slice(0, Math.max(0, room)).map((item) => item.handle);
      room -= handles.length;
      return { d: list.id, n: list.name || '', s: list.store || '', i: handles };
    });
  const since = Date.now() - CARRY_REMOVED_DAYS * 864e5;
  const removed = Object.fromEntries(Object.entries(wishlist.removed)
    .filter(([, time]) => Number(time) > since)
    .sort((a, b) => Number(b[1]) - Number(a[1]))
    .slice(0, CARRY_MAX_REMOVED));
  const worth = lists.some((list) => list.i.length > 0 || list.n) || Object.keys(removed).length > 0;
  // Seit 1.2.4 ein Objekt { l: Listen, r: Gelöschtes }; vorher nur die Liste der Listen (wird drüben weiter verstanden)
  return worth ? toBase64Url(JSON.stringify({ v: 2, l: lists, r: removed })) : '';
}

// Unmittelbar vor dem Absenden (auch bei requestSubmit), damit immer der aktuelle Stand mitgeht
document.addEventListener('submit', (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !/\/localization$/.test(form.getAttribute('action') || '')) return;

  const input = form.querySelector('input[name="return_to"]');
  if (!input) return;

  try {
    const url = new URL(input.value || window.location.pathname, window.location.origin);
    url.searchParams.delete(CARRY_PARAM);
    // Angemeldet: die Listen kommen drüben aus dem Kundenkonto. Mitgeschickt landeten sie dort als Gast-Listen und
    // blieben nach dem Abmelden liegen (1.2.4, Fehlerliste Nr. 8)
    const value = config.customer ? '' : carryValue();
    if (value) url.searchParams.set(CARRY_PARAM, value);
    input.value = `${url.pathname}${url.search}${url.hash}`;
  } catch (error) {
    /* ohne Listen weiter */
  }
}, true);

// Nur Links annehmen, die gerade von der anderen DUK-Domain kommen (wie der Warenkorb in assets/duk-cart.js)
function cameFromOtherShop() {
  try {
    const host = new URL(document.referrer).hostname;
    return host !== window.location.hostname && /(^|\.)duftundkultur\.(at|com)$|\.myshopify\.com$/.test(host);
  } catch (error) {
    return false;
  }
}

function importCarried() {
  const url = new URL(window.location.href);
  const value = url.searchParams.get(CARRY_PARAM);
  if (!value) return;

  url.searchParams.delete(CARRY_PARAM);
  window.history.replaceState(window.history.state, '', url.toString());
  if (!cameFromOtherShop()) return;

  let incoming;
  try {
    incoming = JSON.parse(fromBase64Url(value));
  } catch (error) {
    return;
  }
  const entries = Array.isArray(incoming) ? incoming : incoming && Array.isArray(incoming.l) ? incoming.l : null;
  if (!entries) return;

  const now = Date.now();
  const lists = entries
    .filter((entry) => entry && typeof entry === 'object')
    .map((entry) => ({
      id: cleanId(entry.d) || uid(),
      name: cleanName(entry.n),
      store: cleanStore(entry.s),
      created: now,
      items: (Array.isArray(entry.i) ? entry.i : []).filter(validHandle).map((handle, index) => ({ handle, id: null, added: now - index }))
    }));

  // Was drüben gelöscht wurde, bleibt auch hier weg (nur, wenn es hier nicht später wieder dazukam)
  const removed = mergeRemoved(wishlist.removed, Array.isArray(incoming) ? null : incoming.r);
  wishlist.state.removed = removed;
  wishlist.state.lists = withDefaults(applyRemoved(mergeLists(wishlist.state.lists, lists), removed));
  wishlist.markDirty();
  localStore.save(wishlist.state);
}

importCarried();

window.DukWishlist = {
  get lists() { return JSON.parse(JSON.stringify(wishlist.lists)); },
  get items() { return wishlist.lists.flatMap((list) => list.items.map((item) => ({ ...item, list: list.id }))); },
  get syncStatus() { return sync.status; },
  has: (handle) => wishlist.has(handle),
  add: (handle, listId, data) => Boolean(wishlist.add(handle, listId, data)),
  remove: (handle, listId = MAIN) => Boolean(wishlist.remove(handle, listId)),
  createList: (name, store) => wishlist.createList(name, store)?.id || null,
  suggestions: (date) => picker.suggestions(date),
  openPicker: (handle, title) => picker.open({ handle, title })
};

/* ---------------------------------------------------------------------------------------------- */
/* Product data and Klaviyo                                                                        */
/* ---------------------------------------------------------------------------------------------- */

const productCache = new Map();

const fetchProduct = (root, handle) =>
  fetch(`${root}products/${encodeURIComponent(handle)}.js`, { headers: { Accept: 'application/json' } })
    .then((response) => (response.ok ? response.json() : null))
    .catch(() => null);

// Produktdaten für Klaviyo immer aus dem deutschen Shop (Wurzel ohne /en, auf .at und .com gleich), damit Name und Typ
// nicht je nach Sprache wechseln (1.2.4, Matthias 08.10.). Klappt das nicht, die Seite in der aktuellen Sprache.
function productJson(handle) {
  if (!productCache.has(handle)) {
    const request = fetchProduct('/', handle).then((product) => product || (routesRoot() !== '/' ? fetchProduct(routesRoot(), handle) : null));
    productCache.set(handle, request);
  }

  return productCache.get(handle);
}

// Listenname und -art für Klaviyo, immer deutsch: „Wunschliste“, „Vor Ort probieren“, Vorschläge mit dem deutschen
// Namen („Body Care“ wird „Pflege“), eigene Namen wie eingegeben
function klaviyoList(list) {
  if (!list || list.id === MAIN) return { name: STRINGS.de.mainList, type: 'Wunschliste' };
  if (list.id === TRY) return { name: STRINGS.de.tryList, type: 'Vor Ort probieren' };

  const key = nameKey(list.name);
  if (key.startsWith('idea:')) return { name: suggestedLabels('de')[key.slice(5)] || list.name, type: 'Vorschlag' };

  return { name: String(list.name || '').trim() || STRINGS.de.unnamed, type: 'Eigene Liste' };
}

// Marketing consent from the Shopify Customer Privacy API. No API or no answer counts as no consent.
function marketingAllowed() {
  try {
    return window.Shopify?.customerPrivacy?.marketingAllowed?.() === true;
  } catch (error) {
    return false;
  }
}

/* Anonymous count for the shop: product ID and list type only, nothing about the person. Several adds within
   a moment go out together; leaving the page sends what is waiting. Never blocks or breaks the wishlist. */
const counter = {
  queue: [],
  timer: 0,

  add(productId, list, via) {
    if (!config.syncUrl || sessionGet(SYNC_SESSION_KEY) === 'off') return;

    const id = Number(productId);
    if (!Number.isSafeInteger(id) || id <= 0) return;

    const type = !list || list.id === MAIN ? 'main' : list.id === TRY ? 'try' : 'own';
    this.queue.push(via ? { p: id, l: type, v: via } : { p: id, l: type });

    if (this.queue.length >= COUNT_MAX) {
      this.send();
      return;
    }

    window.clearTimeout(this.timer);
    this.timer = window.setTimeout(() => this.send(), COUNT_DELAY);
  },

  send() {
    window.clearTimeout(this.timer);

    while (this.queue.length > 0) {
      const adds = this.queue.splice(0, COUNT_MAX);

      try {
        fetch(`${config.syncUrl}/count`, {
          method: 'POST',
          keepalive: true,
          credentials: 'same-origin',
          cache: 'no-store',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ v: 1, adds })
        }).catch(() => {});
      } catch (error) {
        /* counting must never break the wishlist */
      }
    }
  }
};

window.addEventListener('pagehide', () => counter.send());
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') counter.send();
});

async function afterAdd(handle, list, via) {
  const product = await productJson(handle);
  if (!product) return;

  if (product.id) wishlist.update(handle, { id: product.id });

  counter.add(product.id, list, via);

  const klaviyo = window.klaviyo;
  if (!klaviyo) return;
  if (!marketingAllowed()) return;

  const image = product.featured_image ? String(product.featured_image) : '';
  const target = klaviyoList(list);
  const properties = {
    ProductName: product.title,
    ProductID: product.id,
    Handle: product.handle || handle,
    Brand: product.vendor,
    Categories: product.type ? [product.type] : [],
    Price: typeof product.price === 'number' ? product.price / 100 : null,
    CompareAtPrice: typeof product.compare_at_price === 'number' && product.compare_at_price > 0 ? product.compare_at_price / 100 : null,
    URL: `${window.location.origin}/products/${product.handle || handle}`,
    ImageURL: image.startsWith('//') ? `https:${image}` : image,
    List: target.name,
    ListType: target.type,
    Language: config.locale,
    Source: 'Wunschliste'
  };

  // Only known profiles are tracked by Klaviyo itself; never wait for the result
  try {
    if (typeof klaviyo.track === 'function') {
      klaviyo.track(KLAVIYO_EVENT, properties);
    } else if (typeof klaviyo.push === 'function') {
      klaviyo.push(['track', KLAVIYO_EVENT, properties]);
    }
  } catch (error) {
    /* tracking must never break the wishlist */
  }
}

/* ---------------------------------------------------------------------------------------------- */
/* Toast actions (undo, change list). The toast may live in a shadow root: use the composed path. */
/* ---------------------------------------------------------------------------------------------- */

const undoEntries = new Map();
let undoCounter = 0;

function undoToast(text, entry) {
  const token = String(++undoCounter);
  undoEntries.set(token, entry);
  toast(`${escapeHtml(text)} <button type="button" class="link" data-duk-wishlist-undo="${token}">${escapeHtml(t('undo'))}</button>`, 5000);
}

function removedToast(handle, list, entry) {
  undoToast(list.id === MAIN ? t('removedToastMain') : t('removedToast', { list: wishlist.name(list) }), { handle, listId: list.id, entry });
}

function deletedToast({ list, index }) {
  undoToast(t('deletedToast', { list: wishlist.name(list) }), { deleted: list, index });
}

// Leaving the page with the sheet still open: the product stays saved, Klaviyo still hears about it
window.addEventListener('pagehide', () => {
  if (picker.options) picker.flush();
});

document.addEventListener('click', (event) => {
  const path = typeof event.composedPath === 'function' ? event.composedPath() : [event.target];
  const element = path.find((node) => node instanceof Element && (node.hasAttribute('data-duk-wishlist-undo') || node.hasAttribute('data-duk-wishlist-pick')));
  if (!element) return;

  event.preventDefault();

  if (element.hasAttribute('data-duk-wishlist-undo')) {
    const undo = undoEntries.get(element.getAttribute('data-duk-wishlist-undo'));
    undoEntries.delete(element.getAttribute('data-duk-wishlist-undo'));

    if (undo && undo.deleted) {
      wishlist.restoreList(undo.deleted, undo.index);
    } else if (undo && wishlist.list(undo.listId)) {
      wishlist.add(undo.handle, undo.listId, undo.entry);
    }
  } else {
    // „Liste wählen“ nach dem Herz (1.2.4, Matthias 07.10., 23:12): liegt das Produkt nur auf der Wunschliste, wandert es
    // in die erste andere Liste, die man wählt, wie beim Herz mit Auswahl. Klaviyo hat das Merken schon gehört.
    const handle = element.getAttribute('data-duk-wishlist-pick');
    const lists = wishlist.listsWith(handle);
    picker.open(lists.length === 1 && lists[0].id === MAIN ? { handle, provisional: MAIN, tracked: true } : { handle });
  }
});

/* ---------------------------------------------------------------------------------------------- */
/* List picker (one <dialog> for the whole page)                                                    */
/* ---------------------------------------------------------------------------------------------- */

const picker = {
  dialog: null,
  options: null,
  selectedStore: '',
  ideas: {},
  added: new Set(),

  build() {
    const dialog = document.createElement('dialog');
    dialog.className = `duk-wl-sheet color-scheme color-scheme--${config.scheme}`;
    dialog.setAttribute('aria-labelledby', 'duk-wl-sheet-title');
    // 1.2.4 (Matthias, 07.10., Canvas 16/16b/16c): oben das Produkt mit ×, ohne Überschrift darüber; darunter klein
    // „Deine Listen“ und „Neue Liste“ mit gestrichelten Vorschlägen. Nur die Mitte scrollt, „Fertig“ bleibt unten.
    // Am Handy von unten, am Desktop als Seitenleiste von rechts wie der Warenkorb.
    dialog.innerHTML = `
      <div class="duk-wl-sheet__inner">
        <div class="duk-wl-sheet__head">
          <div class="duk-wl-sheet__product" id="duk-wl-sheet-product" tabindex="-1" data-product hidden></div>
          <p class="h6" id="duk-wl-sheet-title" tabindex="-1" data-title></p>
          <button type="button" class="duk-wl-sheet__close" data-close aria-label="${escapeHtml(t('close'))}">${closeSvg}</button>
        </div>
        <div class="duk-wl-sheet__body">
          <div class="duk-wl-sheet__section" data-lists-section>
            <p class="duk-wl-sheet__label">${escapeHtml(t('pickerTitle'))}</p>
            <div class="duk-wl-sheet__lists" data-lists></div>
          </div>
          <div class="duk-wl-sheet__new" data-new>
            <p class="duk-wl-sheet__label" data-new-label>${escapeHtml(t('newList'))}</p>
            <form class="duk-wl-sheet__form" data-new-form hidden novalidate>
              <div class="duk-wl-sheet__custom">
                <input id="duk-wl-sheet-list" class="input" type="text" maxlength="${LIMITS.name}" autocomplete="off" autocorrect="off" enterkeyhint="done" placeholder="${escapeHtml(t('customList'))}" aria-label="${escapeHtml(t('customList'))}" data-1p-ignore data-lpignore="true" data-name>
                <button type="submit" class="button button--secondary" data-create>${escapeHtml(t('create'))}</button>
              </div>
            </form>
            <div class="duk-wl-chips duk-wl-chips--new" data-suggestions hidden></div>
          </div>
          <div class="duk-wl-sheet__stores" data-store-mode hidden>
            <div class="duk-wl-chips" data-store-chips></div>
          </div>
        </div>
        <div class="duk-wl-sheet__foot" data-foot>
          <button type="button" class="button w-full" data-done>${escapeHtml(t('done'))}</button>
          <a href="${escapeHtml(config.pageUrl)}" class="duk-wl-sheet__view link text-sm" data-view>${escapeHtml(t('viewWishlist'))}</a>
        </div>
      </div>`;

    dialog.addEventListener('click', this.onClick);
    dialog.addEventListener('submit', this.onSubmit);
    dialog.addEventListener('close', () => this.handleClose());

    // A click on the backdrop closes the sheet
    dialog.addEventListener('pointerdown', (event) => {
      if (event.target === dialog) dialog.close();
    });

    document.body.append(dialog);
    this.dialog = dialog;
  },

  part(name) {
    return this.dialog.querySelector(`[data-${name}]`);
  },

  // options: { handle, title } | { mode: 'create', onCreate } | { mode: 'store', listId }
  open(options = {}) {
    if (!this.dialog) this.build();

    const mode = options.mode || 'product';
    if (mode === 'product' && !validHandle(options.handle)) return;

    if (this.options) this.flush();
    this.options = { ...options, mode };
    this.added = new Set(options.provisional && !options.tracked ? [options.provisional] : []);
    // Lists the product was in before the heart was tapped (the heart may have saved it a moment ago), for the
    // message after closing
    this.before = mode === 'product'
      ? new Set(options.provisional && !options.tracked ? [] : wishlist.listsWith(options.handle).map((list) => list.id))
      : new Set();
    this.selectedStore = mode === 'store' ? (wishlist.list(options.listId)?.store || '') : '';
    this.ideas = {};

    // Primary note, scent family and product group come in a moment (product page attributes or card section)
    if (mode === 'product') {
      const handle = options.handle;
      productIdeas(handle).then((ideas) => {
        if (!this.options || this.options.handle !== handle) return;
        this.ideas = ideas;
        this.render();
      });
    }

    this.part('name').value = '';
    this.part('new-form').hidden = mode !== 'create';
    this.render();

    if (typeof this.dialog.showModal === 'function') {
      if (!this.dialog.open) this.dialog.showModal();
    } else {
      this.dialog.setAttribute('open', '');
    }

    // Focus on the product (or the title), not on × or a list: nothing is ringed, the screen reader starts at the top
    const start = mode === 'product' ? this.part('product') : this.part('title');
    if (start && !start.hidden) start.focus({ preventScroll: true });
  },

  close() {
    if (!this.dialog) return;

    if (typeof this.dialog.close === 'function') {
      this.dialog.close(); // fires "close"
    } else {
      this.dialog.removeAttribute('open');
      this.handleClose();
    }
  },

  handleClose() {
    const options = this.options;
    this.flush();
    this.options = null;
    if (options && options.mode === 'product') this.confirm(options.handle, this.before || new Set());
    if (options && options.onClose) options.onClose();
  },

  // Nach dem Schließen sagen, was passiert ist (1.2.4, Matthias/Hanna 07.10.: nach „Fertig“ war unklar, ob es geklappt
  // hat). Gleiche Regel wie die Zeile beim Herz: die Wunschliste ist ein Ort, eigene Listen haben einen Namen.
  confirm(handle, before) {
    const now = wishlist.listsWith(handle);
    const after = new Set(now.map((list) => list.id));
    const same = before.size === after.size && [...before].every((id) => after.has(id));
    if (same) return;

    if (now.length > 0) {
      const text = now.length > 1
        ? t('savedToastMany', { n: now.length })
        : now[0].id === MAIN ? t('savedToastMain') : t('savedToast', { list: wishlist.name(now[0]) });
      const url = now.length > 1 ? `${config.pageUrl}?${LIST_PARAM}=${OVERVIEW}` : listUrl(now[0].id);
      toast(`${escapeHtml(text)} <a href="${escapeHtml(url)}" class="link">${escapeHtml(t('view'))}</a>`, 5000);

      // Das Herz pulsiert erst jetzt, wo man es sieht
      document.querySelectorAll(`duk-wishlist-button[handle="${cssValue(handle)}"]`).forEach((button) => {
        if (typeof button.pulse === 'function') button.pulse();
      });
      return;
    }

    const gone = [...before].map((id) => wishlist.list(id)).filter(Boolean);
    const text = gone.length > 1
      ? t('removedToastMany', { n: gone.length })
      : gone.length === 1 && gone[0].id !== MAIN ? t('removedToast', { list: wishlist.name(gone[0]) }) : t('removedToastMain');
    toast(escapeHtml(text), 4000);
  },

  // Klaviyo and the product ID once the choice is made: one event per list the product ended up in
  flush() {
    const handle = this.options && this.options.handle;
    const added = this.added;
    this.added = new Set();
    if (!handle) return;

    added.forEach((id) => {
      const list = wishlist.list(id);
      if (list && wishlist.inList(id, handle)) afterAdd(handle, list);
    });
  },

  // The heart saved the product provisionally: the first other list chosen in this sheet takes its place
  settle(listId) {
    const { handle, provisional } = this.options;
    if (!provisional || provisional === listId) return;

    this.options.provisional = null;
    this.added.delete(provisional);
    if (wishlist.inList(provisional, handle)) wishlist.remove(handle, provisional);
  },

  productTitle(handle) {
    if (this.options.title) return this.options.title;

    const button = document.querySelector(`duk-wishlist-button[handle="${cssValue(handle)}"]`);
    return button ? button.productTitle : '';
  },

  storeChips(selected) {
    return config.stores.map((store) => `<button type="button" class="duk-wl-chip" data-set-store="${escapeHtml(store.id)}" aria-pressed="${store.id === selected}">${escapeHtml(store.place)}</button>`).join('');
  },

  // Occasion in season, „Geschenke“, then by product group (see PERFUME_GROUP above); names already in use
  // are left out, at most four
  suggestions(date = new Date()) {
    const product = Boolean(this.options && this.options.mode === 'product');
    const ideas = product ? this.ideas : {};
    const family = t('families')[ideas.family] || '';
    const perfume = ideas.group === PERFUME_GROUP;
    let familyIdea = perfume ? family : '';

    if (ideas.group === HOME_FRAGRANCE_GROUP && family) {
      const { from, to } = t('homeFamily');
      familyIdea = family.replace(from, to);
    }

    const care = ideas.group === CARE_GROUP;
    const home = !product || HOME_GROUPS.includes(ideas.group);
    const names = [seasonFor(date), t('gifts'), perfume ? ideas.note : '', familyIdea, care ? t('care') : '', home ? t('home') : '']
      .filter(Boolean)
      .map((name) => cleanName(name));
    const seen = new Set(wishlist.lists.map((list) => wishlist.name(list).toLowerCase()));

    return names.filter((name) => {
      const key = name.toLowerCase();
      if (!name || seen.has(key)) return false;
      seen.add(key);
      return true;
    }).slice(0, 4);
  },

  render() {
    const { mode, handle } = this.options;
    const product = mode === 'product';

    // Mit Produkt steht oben das Produkt statt einer Überschrift (1.2.4); sonst „Neue Liste“ bzw. „Geschäft wählen“
    const titleElement = this.part('title');
    titleElement.textContent = mode === 'store' ? t('chooseStore') : mode === 'create' ? t('newList') : t('pickerTitle');
    titleElement.hidden = product;
    this.dialog.setAttribute('aria-labelledby', product ? 'duk-wl-sheet-product' : 'duk-wl-sheet-title');
    this.dialog.classList.toggle('duk-wl-sheet--product', product);

    // 1.2.6 (Vorschlag Knis, 08.10.): oben nur „Eau de Parfum … hinzufügen“, die Listen mit dem Raster wie in der Übersicht.
    // Das Bild des Produkts steht im Raster jeder Liste, in der es liegt, an erster Stelle: Haken an, Bild drin; Haken aus, Bild weg.
    const productLine = this.part('product');
    const title = product ? this.productTitle(handle) : '';
    productLine.innerHTML = title ? `<span class="duk-wl-sheet__product-text">${escapeHtml(t('addTitle', { title }))}</span>` : '';
    productLine.hidden = !title;

    const lists = this.part('lists');
    this.part('lists-section').hidden = !product;
    // Eine leere Probierliste aus DUK 1.2 zeigt die Auswahl nicht mehr (Probierliste gestrichen, Matthias 07.10.)
    const wanted = [];
    lists.innerHTML = product ? wishlist.inUse.map((list) => {
      const store = storeById(list.store);
      const pressed = wishlist.inList(list.id, handle);
      const others = list.items.map((item) => item.handle).filter((entry) => entry !== handle);
      const shown = (pressed ? [handle, ...others] : others).slice(0, 4);
      wanted.push(...shown);
      return `<button type="button" class="duk-wl-row duk-wl-row--grid" data-list="${escapeHtml(list.id)}" aria-pressed="${pressed}">
          ${pickerSurfaceHtml(shown, list.items.length)}
          <span class="duk-wl-row__text">
            <span class="duk-wl-row__name">${escapeHtml(wishlist.name(list))}</span>
            ${store ? `<span class="duk-wl-row__meta">${escapeHtml(store.place)}</span>` : ''}
          </span>
          <span class="duk-wl-row__box">${checkSvg}</span>
        </button>`;
    }).join('') : '';
    if (product) loadThumbs([handle, ...wanted], this.dialog);

    // Everything that makes a new list sits under one label: the suggestions and „+ Eigener Name“ (opens the field).
    // On the wishlist page („+ Neue Liste“, no product) the sheet title says it already and the field is open.
    this.part('new').hidden = mode === 'store';
    this.part('new-label').hidden = mode !== 'product';
    const formOpen = !this.part('new-form').hidden;
    const chips = mode === 'store' ? [] : this.suggestions().map((name) => `<button type="button" class="duk-wl-chip" data-suggestion="${escapeHtml(name)}">+ ${escapeHtml(name)}</button>`);
    // Alle Vorschläge gleich, gestrichelt wie die Kachel „Neue Liste“ (gestrichelt = neu anlegen), auch „+ Eigener Name“
    if (product && !formOpen) chips.unshift(`<button type="button" class="duk-wl-chip" data-new-toggle>+ ${escapeHtml(t('ownName'))}</button>`);
    const suggestions = this.part('suggestions');
    suggestions.innerHTML = chips.join('');
    suggestions.hidden = chips.length === 0;

    this.part('store-mode').hidden = mode !== 'store';
    this.part('store-chips').innerHTML = mode === 'store' ? this.storeChips(this.selectedStore) : '';

    // With a product, „Fertig“ is the one action (it saves a typed name); „Anlegen“ only on the list page
    this.part('foot').hidden = !product;
    this.part('done').hidden = !product;
    this.part('create').hidden = product;

    const view = this.part('view');
    view.hidden = !product;
    view.href = listUrl(wishlist.lastId);
  },

  onClick: (event) => {
    const target = event.target.closest('button');
    if (!target || !picker.options) return;

    const { mode, handle } = picker.options;

    if (target.hasAttribute('data-close')) {
      picker.close();
      return;
    }

    // „Fertig“ also saves a name typed into the field, so nothing typed gets lost
    if (target.hasAttribute('data-done')) {
      const name = picker.part('new-form').hidden ? '' : cleanName(picker.part('name').value);
      if (name) picker.createNamed(name);
      picker.close();
      return;
    }

    if (target.hasAttribute('data-list')) {
      const listId = target.getAttribute('data-list');

      if (wishlist.inList(listId, handle)) {
        wishlist.remove(handle, listId);
        picker.added.delete(listId);
        if (picker.options.provisional === listId) picker.options.provisional = null;
      } else if (wishlist.add(handle, listId)) {
        picker.added.add(listId);
        picker.settle(listId);
      }

      picker.render();
      // Fokus bleibt auf der Zeile (die Zeilen werden neu gezeichnet), sonst landet er im Leeren (1.2.4)
      picker.dialog.querySelector(`[data-list="${cssValue(listId)}"]`)?.focus({ preventScroll: true });
      return;
    }

    // Am Handy kein automatischer Fokus, die Tastatur würde die Vorschläge verdecken; am Desktop gleich ins Feld (1.2.4)
    if (target.hasAttribute('data-new-toggle')) {
      picker.part('new-form').hidden = false;
      picker.render();
      if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) picker.part('name').focus();
      return;
    }

    // A suggestion creates its list right away (and saves the product there)
    if (target.hasAttribute('data-suggestion')) {
      picker.createNamed(cleanName(target.getAttribute('data-suggestion')));
      return;
    }

    if (target.hasAttribute('data-set-store') && mode === 'store') {
      wishlist.setStore(picker.options.listId, target.getAttribute('data-set-store'));
      picker.close();
    }
  },

  onSubmit: (event) => {
    event.preventDefault();
    if (!picker.options) return;

    const input = picker.part('name');
    const name = cleanName(input.value);

    if (!name) {
      input.focus();
      return;
    }

    picker.createNamed(name);
  },

  // New list from a suggestion or the own name; with a product in view it goes straight into that list
  createNamed(name) {
    if (!name || !this.options) return;

    const { mode, handle, onCreate } = this.options;

    // A name that is already a list (also „Probierliste“ in the other language) opens that list instead of a twin
    const existing = wishlist.lists.find((entry) => wishlist.name(entry).toLowerCase() === name.toLowerCase()) || (isTryName(name) ? wishlist.list(TRY) : null);
    const list = existing || wishlist.createList(name);
    if (!list) return;

    this.part('name').value = '';
    this.part('name').blur();

    if (mode === 'product') {
      if (!wishlist.inList(list.id, handle) && wishlist.add(handle, list.id)) this.added.add(list.id);
      if (wishlist.inList(list.id, handle)) this.settle(list.id);

      this.part('new-form').hidden = true;
      this.render();
      return;
    }

    this.close();
    if (onCreate) onCreate(list);
  }
};

/* ---------------------------------------------------------------------------------------------- */
/* Fenster zu einer Liste: Umbenennen über den Stift an einer Kachel (Matthias, 06.10.)            */
/* ---------------------------------------------------------------------------------------------- */

// Ein Fenster wie „Deine Listen“ (am Handy von unten): Alle in den Warenkorb, Teilen, Umbenennen, Löschen. Es erscheint
// nur, was zur Liste passt: Wunschliste ohne Löschen, „Vor Ort probieren“ ohne Umbenennen und Warenkorb, leere Listen
// nur Umbenennen und Löschen. Löschen fragt nach und lässt sich rückgängig machen.
const listMenu = {
  dialog: null,
  page: null,
  listId: null,
  mode: 'menu',

  build() {
    const dialog = document.createElement('dialog');
    dialog.className = `duk-wl-sheet color-scheme color-scheme--${config.scheme}`;
    dialog.setAttribute('aria-labelledby', 'duk-wl-menu-title');
    dialog.innerHTML = `
      <div class="duk-wl-sheet__inner">
        <div class="duk-wl-sheet__head">
          <p class="h6" id="duk-wl-menu-title" tabindex="-1" autofocus style="outline: none" data-title></p>
          <button type="button" class="duk-wl-sheet__close" data-menu-action="close" aria-label="${escapeHtml(t('close'))}">${closeSvg}</button>
        </div>
        <div class="duk-wl-sheet__body" data-body></div>
      </div>`;

    dialog.addEventListener('click', (event) => this.onClick(event));
    dialog.addEventListener('submit', (event) => this.onSubmit(event));
    dialog.addEventListener('pointerdown', (event) => {
      if (event.target === dialog) dialog.close();
    });

    document.body.append(dialog);
    this.dialog = dialog;
  },

  open(page, listId, mode = 'menu') {
    if (!wishlist.list(listId)) return;
    if (!this.dialog) this.build();

    this.page = page;
    this.listId = listId;
    this.mode = mode;
    this.render();

    if (typeof this.dialog.showModal === 'function') {
      if (!this.dialog.open) this.dialog.showModal();
    } else {
      this.dialog.setAttribute('open', '');
    }
  },

  close() {
    if (!this.dialog) return;
    if (typeof this.dialog.close === 'function') this.dialog.close();
    else this.dialog.removeAttribute('open');
  },

  render() {
    const list = wishlist.list(this.listId);
    if (!list) {
      this.close();
      return;
    }

    const count = list.items.length;
    const tryList = list.id === TRY;
    const body = this.dialog.querySelector('[data-body]');
    this.dialog.querySelector('[data-title]').textContent = this.mode === 'rename' ? t('rename') : wishlist.name(list);

    if (this.mode === 'rename') {
      body.innerHTML = `
        <form class="duk-wl-sheet__form" data-menu-rename novalidate>
          <div class="duk-wl-sheet__custom">
            <input class="input" type="text" maxlength="${LIMITS.name}" autocomplete="off" autocorrect="off" enterkeyhint="done" aria-label="${escapeHtml(t('rename'))}" data-1p-ignore data-lpignore="true" value="${escapeHtml(wishlist.name(list))}">
            <button type="submit" class="button button--secondary" data-create>${escapeHtml(t('save'))}</button>
          </div>
        </form>`;
      body.querySelector('input').focus();
      return;
    }

    if (this.mode === 'delete') {
      const question = count > 0 ? t('deleteConfirm', { list: wishlist.name(list), count: countLabel(count) }) : t('deleteConfirmEmpty', { list: wishlist.name(list) });
      body.innerHTML = `
        <div class="duk-wl-menu__confirm">
          <p>${escapeHtml(question)}</p>
          <div class="duk-wl-menu__buttons">
            <button type="button" class="button" data-menu-action="delete-confirm">${escapeHtml(t('delete'))}</button>
            <button type="button" class="duk-wl-action" data-menu-action="menu">${escapeHtml(t('cancel'))}</button>
          </div>
        </div>`;
      return;
    }

    const actions = [];
    if (count > 0 && !tryList) actions.push(['cart', t('addAll')]);
    if (count > 0) actions.push(['share', t('share')]);
    if (!tryList) actions.push(['rename', t('rename')]);
    if (tryList && config.stores.length > 1) actions.push(['store', t('changeStore')]);
    if (list.id !== MAIN) actions.push(['delete', t('delete')]);

    body.innerHTML = `<div class="duk-wl-menu">${actions.map(([action, label]) => `<button type="button" class="duk-wl-menu__item" data-menu-action="${action}">${escapeHtml(label)}</button>`).join('')}</div>`;
  },

  onClick(event) {
    const button = event.target.closest('[data-menu-action]');
    if (!button) return;

    const list = wishlist.list(this.listId);
    const action = button.getAttribute('data-menu-action');

    if (action === 'close' || !list) {
      this.close();
      return;
    }

    if (action === 'menu' || action === 'rename' || action === 'delete') {
      this.mode = action;
      this.render();
      return;
    }

    this.close();

    if (action === 'cart' && this.page) this.page.addListToCart(null, list);
    if (action === 'share' && this.page) this.page.share(list);
    if (action === 'store') picker.open({ mode: 'store', listId: list.id });

    if (action === 'delete-confirm') {
      const deleted = wishlist.deleteList(list.id);
      if (deleted) deletedToast(deleted);
    }
  },

  onSubmit(event) {
    if (!event.target.matches('[data-menu-rename]')) return;

    event.preventDefault();
    const list = wishlist.list(this.listId);
    const name = cleanName(event.target.querySelector('input').value);

    // Die Wunschliste behält ihren Namen, wenn das Feld leer bleibt
    if (list && (name || list.id === MAIN)) {
      wishlist.renameList(list.id, list.id === MAIN && name === t('mainList') ? '' : name);
    }

    this.close();
  }
};

/* ---------------------------------------------------------------------------------------------- */
/* Heart button                                                                                    */
/* ---------------------------------------------------------------------------------------------- */

class DukWishlistButton extends HTMLElement {
  connectedCallback() {
    this.handle = (this.getAttribute('handle') || '').trim();

    if (!this.button) {
      this.button = this.querySelector('button') || this.createButton();
      this.button.addEventListener('click', this.onClick);
    }

    document.addEventListener(CHANGE_EVENT, this.onChange);
    this.update();
  }

  disconnectedCallback() {
    document.removeEventListener(CHANGE_EVENT, this.onChange);
  }

  createButton() {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'duk-wishlist-button';
    button.innerHTML = heartSvg();
    this.append(button);
    return button;
  }

  // On the wishlist page the heart belongs to the list that is shown
  get context() {
    const holder = this.closest('[data-duk-wishlist-context]');
    const id = holder ? holder.getAttribute('data-duk-wishlist-context') : '';
    return id && wishlist.list(id) ? id : null;
  }

  get productTitle() {
    if (this.hasAttribute('product-title')) return this.getAttribute('product-title');

    const title = this.closest('product-card')?.querySelector('.product-title');
    return title ? title.textContent.trim() : '';
  }

  update() {
    const context = this.context;
    const lists = context ? wishlist.listsWith(this.handle).filter((list) => list.id === context) : wishlist.listsWith(this.handle);
    const active = lists.length > 0;
    const label = this.querySelector('[data-duk-wishlist-label]');

    this.toggleAttribute('active', active);
    this.button.setAttribute('aria-pressed', String(active));

    if (label) {
      let text = t('add');

      if (active && lists.length > 1) {
        text = t('savedInMany', { n: lists.length });
      } else if (active) {
        text = lists[0].id === MAIN ? t('saved') : t('savedIn', { list: wishlist.name(lists[0]) });
      }

      label.textContent = text;
      return;
    }

    let action = t('add');
    if (active) action = context || wishlist.inUse.length <= 1 ? t('remove') : t('edit');

    const title = this.productTitle;
    this.button.setAttribute('aria-label', title ? `${action}: ${title}` : action);
  }

  onChange = (event) => {
    const handle = event.detail && event.detail.handle;
    if (!handle || handle === this.handle) this.update();
  };

  pulse() {
    this.button.classList.remove('is-pulsing');
    void this.button.offsetWidth; // restart the animation
    this.button.classList.add('is-pulsing');
  }

  onClick = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (!validHandle(this.handle)) return;

    const context = this.context;

    // Wishlist page: the heart adds to or removes from the list on screen
    if (context) {
      const list = wishlist.list(context);

      if (wishlist.inList(context, this.handle)) {
        const entry = wishlist.remove(this.handle, context);
        if (entry) removedToast(this.handle, list, entry);
      } else if (wishlist.add(this.handle, context, { id: this.getAttribute('product-id') })) {
        this.pulse();
        afterAdd(this.handle, list);
      }

      return;
    }

    // 1.2.4 (Knis und Matthias, 07.10., „wer keine Liste dazuklickt, hat nie ein Overlay“): Mit nur der Wunschliste
    // öffnet das Herz kein Fenster. Antippen = sofort gemerkt, kurze Meldung mit „Liste wählen“ für alle, die sortieren
    // wollen; nochmal antippen = entfernt, mit „Rückgängig“. Erst ab der zweiten Liste geht wie bisher die Auswahl auf.
    if (wishlist.inUse.length <= 1) {
      const main = wishlist.list(MAIN);

      if (wishlist.inList(MAIN, this.handle)) {
        const entry = wishlist.remove(this.handle, MAIN);
        if (entry && main) removedToast(this.handle, main, entry);
        return;
      }

      const list = wishlist.add(this.handle, MAIN, { id: this.getAttribute('product-id') });
      if (!list) return; // voll: wishlist.add meldet es

      this.pulse();
      afterAdd(this.handle, list);
      toast(`${escapeHtml(t('savedToastMain'))} <button type="button" class="link" data-duk-wishlist-pick="${escapeHtml(this.handle)}">${escapeHtml(t('pickList'))}</button>`, 5000);
      return;
    }

    // Tap = saved right away (provisionally) in the list of this visit, then the picker opens to choose lists.
    // Closing the picker keeps it saved; another list moves it there; unticking every list removes it.
    // Klaviyo hears about it when the picker closes, with the list the product ended up in.
    if (!wishlist.has(this.handle)) {
      const list = wishlist.add(this.handle, wishlist.lastId, { id: this.getAttribute('product-id') });

      // Liste voll: die Auswahl geht trotzdem auf, eine andere Liste hat vielleicht Platz (1.2.4, Fehlerliste Nr. 12)
      picker.open({ handle: this.handle, title: this.productTitle, provisional: list ? list.id : null });
      return;
    }

    picker.open({ handle: this.handle, title: this.productTitle });
  };
}

if (!window.customElements.get('duk-wishlist-button')) {
  window.customElements.define('duk-wishlist-button', DukWishlistButton);
}

/* ---------------------------------------------------------------------------------------------- */
/* Hearts on product cards (theme cards, Algolia cards, cards loaded later)                        */
/* ---------------------------------------------------------------------------------------------- */

function enhanceCards() {
  document.querySelectorAll(CARD_SELECTOR).forEach((card) => {
    if (card.closest(SKIP_SELECTOR)) return;

    const figure = card.querySelector('.product-card__figure');
    const handle = (card.getAttribute('handle') || '').trim();

    if (!figure || !validHandle(handle) || figure.querySelector(':scope > duk-wishlist-button')) return;

    const button = document.createElement('duk-wishlist-button');
    button.className = 'duk-wishlist-card-button';
    button.setAttribute('handle', handle);
    figure.append(button);
  });
}

function updateHeader() {
  const empty = wishlist.total() === 0;
  document.querySelectorAll('[data-duk-wishlist-dot]').forEach((dot) => { dot.hidden = empty; });
}

// A timer instead of requestAnimationFrame: frames pause in background tabs, cards must still get their heart
let enhanceScheduled = false;

function scheduleEnhance() {
  if (enhanceScheduled) return;

  enhanceScheduled = true;
  window.setTimeout(() => {
    enhanceScheduled = false;
    enhanceCards();
    updateHeader();
  }, 0);
}

document.addEventListener(CHANGE_EVENT, updateHeader);
new MutationObserver(scheduleEnhance).observe(document.documentElement, { childList: true, subtree: true });
enhanceCards();
updateHeader();

/* ---------------------------------------------------------------------------------------------- */
/* Wishlist page                                                                                   */
/* ---------------------------------------------------------------------------------------------- */

const cardCache = new Map();

function fetchCard(handle) {
  if (!cardCache.has(handle)) {
    const request = fetch(`${routesRoot()}products/${encodeURIComponent(handle)}?section_id=${CARD_SECTION}`, { headers: { Accept: 'text/html' } })
      .then(async (response) => {
        if (response.status === 404) return { missing: true };
        if (!response.ok) return { error: true };
        return { html: await response.text() };
      })
      .catch(() => ({ error: true }));

    cardCache.set(handle, request);
  }

  return cardCache.get(handle);
}

async function fetchCards(handles) {
  const results = new Array(handles.length);
  let next = 0;

  const worker = async () => {
    while (next < handles.length) {
      const index = next++;
      results[index] = await fetchCard(handles[index]);
    }
  };

  await Promise.all(Array.from({ length: Math.min(CONCURRENCY, handles.length) }, worker));
  return results;
}

function cardFromHtml(html) {
  const template = document.createElement('template');
  template.innerHTML = html;
  return template.content.querySelector('product-card');
}

// List ideas from the product: primary note („Feigendüfte“), scent family handle (holzig …) and product group.
// The product page heart carries them as attributes, elsewhere they come with the card section
// (snippets/duk-wishlist.liquid, part "ideas").
async function productIdeas(handle) {
  const read = (element, prefix) => ({
    note: (element.getAttribute(`${prefix}note`) || '').replace(/\xad/g, '').trim(),
    family: (element.getAttribute(`${prefix}family`) || '').trim(),
    group: (element.getAttribute(`${prefix}group`) || '').trim()
  });

  const button = document.querySelector(`duk-wishlist-button[handle="${cssValue(handle)}"][group]`);
  if (button) return read(button, '');

  const result = await fetchCard(handle);
  if (!result || !result.html) return {};

  const template = document.createElement('template');
  template.innerHTML = result.html;
  const holder = template.content.querySelector('[data-duk-wishlist-ideas]');
  return holder ? read(holder, 'data-') : {};
}

function sharedFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const value = params.get(SHARE_PARAM);
  if (!value) return null;

  const handles = [...new Set(value.split(',').map((handle) => handle.trim()).filter(validHandle))].slice(0, MAX_SHARED);
  if (handles.length === 0) return null;

  return { handles, name: cleanName(params.get(SHARE_NAME_PARAM) || ''), store: cleanStore(params.get(SHARE_STORE_PARAM) || '') };
}

// Einladung ins Geschäft unter jeder Liste (Matthias, 06.10.): einladend statt praktisch, ohne Route und Öffnungszeiten.
// Das ganze Sortiment gibt es nur in 1010, das steht ausdrücklich da: wer mit der Liste nach Gmunden kommt, wäre enttäuscht.
const inviteStore = () => config.stores.find((store) => store.zip === '1010') || config.stores[0] || null;

function storeBoxHtml(store) {
  const address = store.address || store.name;
  const link = store.url ? `<a href="${escapeHtml(store.url)}" class="link">${escapeHtml(address)}</a>` : escapeHtml(address);

  return `
    <p class="subheading">${escapeHtml(t('storeTitle'))}</p>
    <p class="text-sm">${escapeHtml(t('storeText'))}</p>
    <p class="text-sm">${link}</p>`;
}

const SHARE_ICON = '<svg aria-hidden="true" focusable="false" width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.2"><path d="M8 10.5V1.5M4.8 4.6 8 1.5l3.2 3.1M3 7.5v6.5h10V7.5"/></svg>';
const PLUS_ICON = '<svg aria-hidden="true" focusable="false" width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.3"><path d="M10 3v14M3 10h14"/></svg>';

// Packshot from a wishlist card (section duk-wishlist-card): the theme's first card image, at tile size
function cardImage(html, width = 360) {
  const card = cardFromHtml(html);
  const image = card && (card.querySelector('.product-card__image--primary') || card.querySelector('img'));
  const src = image && image.getAttribute('src');
  if (!src) return '';
  return /[?&]width=\d+/.test(src) ? src.replace(/([?&])width=\d+/, `$1width=${width}`) : `${src}${src.includes('?') ? '&' : '?'}width=${width}`;
}

// Warenkorb-Drawer nach dem Hinzufügen neu laden und öffnen: der ganze Warenkorb samt Drawer-Sektion über cart/update.js,
// dann das Theme-Ereignis „cart:change“ wie beim Hinzufügen auf der Produktseite
async function refreshCartDrawer() {
  const drawer = document.querySelector('cart-drawer');
  const section = drawer && drawer.closest('.shopify-section');
  const sectionId = section ? section.id.replace(/^shopify-section-/, '') : '';

  if (sectionId) {
    try {
      const response = await fetch(`${routesRoot()}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ sections: sectionId, sections_url: window.location.pathname })
      });
      const cart = await response.json();
      if (response.ok && cart.sections && cart.sections[sectionId]) {
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart } }));
      } else {
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      }
    } catch (error) {
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    }
  } else {
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  }

  if (drawer && typeof drawer.show === 'function' && !drawer.hasAttribute('open')) drawer.show();
}

// Kleine Packshots für die Auswahl „Deine Listen“: zuerst aus Produktkarten auf der Seite, sonst aus der Kartensektion
const thumbCache = new Map();

function sizedSrc(src, width) {
  if (!src) return '';
  return /[?&]width=\d+/.test(src) ? src.replace(/([?&])width=\d+/, `$1width=${width}`) : `${src}${src.includes('?') ? '&' : '?'}width=${width}`;
}

function pageCard(handle) {
  return handle ? document.querySelector(`product-card[handle="${cssValue(handle)}"]`) : null;
}

function productVendor(handle) {
  // Produktseite: Marke steht am Herz-Knopf (1.2.4, Kopf des Listen-Fensters wie eine Warenkorb-Zeile)
  const button = handle ? document.querySelector(`duk-wishlist-button[handle="${cssValue(handle)}"][product-vendor]`) : null;
  if (button && button.getAttribute('product-vendor').trim()) return button.getAttribute('product-vendor').trim();

  const vendor = pageCard(handle)?.querySelector('.vendor');
  return vendor ? vendor.textContent.trim() : '';
}

function thumbHtml(handle) {
  const src = handle ? thumbCache.get(handle) : '';
  const inner = src ? `<img src="${escapeHtml(src)}" alt="" width="44" height="51">` : handle ? '' : emptyHeart();
  return `<span class="duk-wl-thumb" aria-hidden="true"${handle ? ` data-thumb="${escapeHtml(handle)}"` : ''}>${inner}</span>`;
}

function loadThumbs(handles, root) {
  const wanted = [...new Set(handles.filter(Boolean))];
  const missing = wanted.filter((handle) => !thumbCache.has(handle));

  missing.forEach((handle) => {
    const image = pageCard(handle)?.querySelector('.product-card__image--primary, img');
    const src = image && image.getAttribute('src');
    if (src) thumbCache.set(handle, sizedSrc(src, 120));
  });

  const paint = () => {
    root.querySelectorAll('[data-thumb]').forEach((element) => {
      const src = thumbCache.get(element.getAttribute('data-thumb'));
      if (src && !element.querySelector('img')) element.innerHTML = `<img src="${escapeHtml(src)}" alt="" width="44" height="51">`;
    });
    // Felder im Raster der Auswahl: Bild vor ein eventuelles „+n“ setzen
    root.querySelectorAll('[data-cell]').forEach((element) => {
      const src = thumbCache.get(element.getAttribute('data-cell'));
      if (src && !element.querySelector('img')) element.insertAdjacentHTML('afterbegin', `<img src="${escapeHtml(src)}" alt="" loading="lazy">`);
    });
  };

  const rest = missing.filter((handle) => !thumbCache.has(handle));
  if (rest.length === 0) {
    paint();
    return;
  }

  fetchCards(rest).then((results) => {
    results.forEach((result, index) => thumbCache.set(rest[index], result && result.html ? cardImage(result.html, 120) : ''));
    paint();
  });
}

// Festes Raster aus vier Feldern wie im Entwurf 2, jedes Produkt in seinem Feld; bei weniger als vier Produkten bleiben
// Felder leer, eine leere Liste zeigt vier leere Felder (Matthias, 06.10.). Ab fünf Produkten ein kleines weißes „+n“ im vierten Feld.
// Ein Produkt steht groß über die ganze Kachel, ab zwei Produkten das Raster (Matthias, 06.10., 16:46, wie im Geschenk-Fenster).
// 1.2.6: Raster in der Auswahl „Meine Listen“ (Vorschlag Knis), Bilder aus dem Packshot-Speicher, fehlende malt loadThumbs nach
function pickerSurfaceHtml(handles, total) {
  const cell = (entry, extra = '') => {
    const src = entry ? thumbCache.get(entry) : '';
    return `<span class="duk-wl-surface__cell"${entry ? ` data-cell="${escapeHtml(entry)}"` : ''}>${src ? `<img src="${escapeHtml(src)}" alt="" loading="lazy">` : ''}${extra}</span>`;
  };

  if (handles.length === 1 && total <= 1) return `<span class="duk-wl-surface duk-wl-surface--one" aria-hidden="true">${cell(handles[0])}</span>`;

  const cells = Array.from({ length: 4 }, (value, index) => cell(handles[index] || '', index === 3 && total > 4 ? `<span class="duk-wl-surface__more">+${total - 4}</span>` : '')).join('');
  return `<span class="duk-wl-surface duk-wl-surface--grid" aria-hidden="true">${cells}</span>`;
}

function surfaceHtml(images, total) {
  const cell = (src, extra = '') => `<span class="duk-wl-surface__cell">${src ? `<img src="${escapeHtml(src)}" alt="" loading="lazy">` : ''}${extra}</span>`;

  if (total === 1) return `<span class="duk-wl-surface duk-wl-surface--one" aria-hidden="true">${cell(images[0] || '')}</span>`;

  const cells = Array.from({ length: 4 }, (value, index) => cell(images[index] || '', index === 3 && total > 4 ? `<span class="duk-wl-surface__more">+${total - 4}</span>` : '')).join('');
  return `<span class="duk-wl-surface duk-wl-surface--grid" aria-hidden="true">${cells}</span>`;
}

class DukWishlistPage extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    const part = (name) => this.querySelector(`[data-duk-wishlist-${name}]`);

    this.back = part('back');
    this.eyebrow = part('eyebrow');
    this.titleElement = part('title');
    this.count = part('count');
    this.actions = part('actions');
    this.intro = part('intro');
    this.panel = part('panel');
    this.overview = part('overview');
    this.storeBox = part('store');
    this.sharedBox = part('shared');
    this.addAllButton = part('add-all');
    this.status = part('status');
    this.empty = part('empty');
    this.emptyTitle = part('empty-title');
    this.emptyHint = part('empty-hint');
    this.list = part('list');
    this.own = part('own');
    this.note = part('note');

    // Die Einladung ins Geschäft steht unter den Produkten, nicht darüber
    if (this.storeBox && this.list) this.list.after(this.storeBox);

    // „Alle in den Warenkorb“ unter Teilen · Umbenennen · Löschen (Matthias, 06.10.)
    this.cartBox = document.createElement('div');
    this.cartBox.className = 'duk-wl-cart';
    this.cartBox.hidden = true;
    this.cartBox.innerHTML = `<button type="button" class="button" data-add-list>${escapeHtml(t('addAll'))}</button>`;
    if (this.actions) this.actions.after(this.cartBox);

    this.pageTitle = this.titleElement ? this.titleElement.textContent.trim() : '';
    this.shared = sharedFromUrl();
    this.renderToken = 0;
    this.panelMode = null;

    this.readUrl();

    this.addEventListener('click', this.onClick);
    this.addEventListener('submit', this.onSubmit);
    document.addEventListener(CHANGE_EVENT, this.onChange);
    document.addEventListener(STATUS_EVENT, this.renderNote);
    window.addEventListener('popstate', this.onPopState);

    this.render();

    if (!config.customer) {
      sync.probe().then((available) => {
        this.loginPossible = available;
        this.renderNote();
      });
    }
  }

  // ?liste=alle: overview; ?liste=<id>: that list; without: overview from two lists with products on, else the list
  readUrl() {
    const requested = new URLSearchParams(window.location.search).get(LIST_PARAM);

    if (requested === OVERVIEW) {
      this.pinned = true;
      this.view = 'overview';
      this.currentId = this.defaultId();
    } else if (requested && wishlist.list(requested)) {
      this.pinned = true;
      this.view = 'list';
      this.currentId = requested;
    } else {
      this.pinned = false;
      this.view = this.autoView();
      this.currentId = this.defaultId();
    }
  }

  // Herz im Menü (1.2.4, Matthias 07.10., 22:25, wie Notino): sobald etwas gemerkt ist, immer die Übersicht, auch mit nur
  // der Wunschliste. Dort steht „+ Neue Liste“, so erfährt man von den Listen; das Herz am Produkt öffnet mit nur einer
  // Liste kein Fenster. Noch gar nichts gemerkt → „Wunschliste ist leer“ mit Erklärung.
  autoView() {
    return wishlist.inUse.some((list) => list.items.length > 0) ? 'overview' : 'list';
  }

  get current() {
    return wishlist.list(this.currentId) || wishlist.list(MAIN);
  }

  get isOverview() {
    return !this.shared && this.view === 'overview';
  }

  // The Wunschliste, or when it is empty the list used last in this visit, else the first list with products
  // (Matthias, 04.10.: first product into „Geschenke“, then the header heart should not open an empty Wunschliste)
  defaultId() {
    if (wishlist.list(MAIN).items.length > 0) return MAIN;

    const last = wishlist.list(wishlist.lastId);
    if (last && last.items.length > 0) return last.id;

    const filled = wishlist.lists.find((list) => list.items.length > 0);
    return filled ? filled.id : MAIN;
  }

  get handles() {
    return this.shared ? this.shared.handles : this.current.items.map((item) => item.handle);
  }

  // Own list a shared link would go into: same name and store, else a new list (or the main list without a name)
  get sharedTarget() {
    if (!this.shared || !this.shared.name) return wishlist.list(MAIN);
    if (isTryName(this.shared.name)) return wishlist.list(TRY);

    const name = this.shared.name.toLowerCase();
    return wishlist.lists.find((list) => list.id !== MAIN && list.name.toLowerCase() === name && list.store === this.shared.store) || null;
  }

  setUrl(value, replace) {
    const url = new URL(window.location.href);
    url.searchParams.set(LIST_PARAM, value);
    if (url.toString() === window.location.href) return;
    window.history[replace ? 'replaceState' : 'pushState'](window.history.state, '', url.toString());
  }

  // Open one list; a new history entry, so the back button of the browser returns to the overview
  select(id, options = {}) {
    if (!wishlist.list(id)) id = MAIN;

    this.pinned = true;
    this.view = 'list';
    this.currentId = id;
    this.panelMode = null;
    this.setUrl(id, options.replace);
    this.render();
    if (!options.keepScroll) window.scrollTo({ top: 0 });
  }

  showOverview(options = {}) {
    this.pinned = true;
    this.view = 'overview';
    this.panelMode = null;
    this.setUrl(OVERVIEW, options.replace);
    this.render();
    if (!options.keepScroll) window.scrollTo({ top: 0 });
  }

  onPopState = () => {
    if (this.shared) return;
    this.panelMode = null;
    this.readUrl();
    this.render();
  };

  listMeta(list) {
    const store = list.id === TRY ? storeById(list.store) : null;
    const count = list.items.length > 0 ? countLabel(list.items.length) : t('emptyMeta');
    return store && store.place ? `${count} · ${store.place}` : count;
  }

  // Head: back link, title, the line below and the actions (share, rename, store, delete)
  renderHead() {
    const overview = this.isOverview;
    const list = this.current;

    if (this.back) this.back.hidden = Boolean(this.shared) || overview;
    if (this.eyebrow) this.eyebrow.hidden = !this.shared;
    if (this.intro) this.intro.hidden = !overview;
    if (this.own) this.own.hidden = !this.shared;

    if (this.shared) {
      this.titleElement.textContent = this.shared.name || t('sharedTitle');
      this.count.textContent = countLabel(this.shared.handles.length);
      this.actions.hidden = true;
      this.cartBox.hidden = true;
      this.panelMode = null;
      this.renderPanel();
      return;
    }

    if (overview) {
      const lists = wishlist.lists.length;
      // Übersicht: „Meine Listen“ statt „Wunschliste“, die ja selbst eine der Kacheln ist (Matthias, 06.10.; „Meine“ statt „Deine“: Knis, 07.10.)
      this.titleElement.textContent = t('pickerTitle');
      this.count.textContent = `${lists === 1 ? t('listsOne') : t('listsMany', { n: lists })} · ${countLabel(wishlist.total())}`;
      this.actions.hidden = true;
      this.cartBox.hidden = true;
      this.panelMode = null;
      this.renderPanel();
      return;
    }

    const count = list.items.length;
    const tryList = list.id === TRY;
    const storePick = tryList && config.stores.length > 1;

    this.titleElement.textContent = wishlist.name(list);
    this.count.textContent = this.listMeta(list);

    // Dieselben Symbole wie an den Kacheln (Matthias, 06.10.); die Wunschliste selbst lässt sich nicht löschen
    const tools = this.toolsHtml(list, false);
    this.actions.classList.add('duk-wl-actions--tools');
    this.actions.innerHTML = tools;
    this.actions.hidden = tools === '';

    // Die Probierliste ist fürs Geschäft, nicht zum Bestellen
    this.cartBox.hidden = count === 0 || tryList;

    this.renderPanel();
  }

  renderPanel() {
    const list = this.current;

    if (this.panelMode === 'rename') {
      this.panel.innerHTML = `
        <form class="duk-wl-panel__form" data-rename-form>
          <div class="form-control">
            <input id="duk-wl-rename" class="input" type="text" maxlength="${LIMITS.name}" autocomplete="off" autocorrect="off" enterkeyhint="done" aria-label="${escapeHtml(t('rename'))}" data-1p-ignore data-lpignore="true" value="${escapeHtml(wishlist.name(list))}">
          </div>
          <button type="submit" class="button button--secondary">${escapeHtml(t('save'))}</button>
          <button type="button" class="duk-wl-action" data-panel-cancel>${escapeHtml(t('cancel'))}</button>
        </form>`;
    } else if (this.panelMode === 'delete') {
      this.panel.innerHTML = `
        <div class="duk-wl-panel__confirm">
          <p>${escapeHtml(list.items.length > 0 ? t('deleteConfirm', { list: wishlist.name(list), count: countLabel(list.items.length) }) : t('deleteConfirmEmpty', { list: wishlist.name(list) }))}</p>
          <button type="button" class="button button--secondary" data-delete-confirm>${escapeHtml(t('delete'))}</button>
          <button type="button" class="duk-wl-action" data-panel-cancel>${escapeHtml(t('cancel'))}</button>
        </div>`;
    } else {
      this.panel.innerHTML = '';
    }

    this.panel.hidden = !this.panelMode;
  }

  // Unter jeder Liste mit Produkten, auch unter geteilten; nicht in der Übersicht und nicht unter leeren Listen
  renderStore() {
    const store = this.isOverview || this.handles.length === 0 ? null : inviteStore();

    this.storeBox.hidden = !store;
    this.storeBox.innerHTML = store ? storeBoxHtml(store) : '';
  }

  renderShared() {
    this.sharedBox.hidden = !this.shared;
    if (!this.shared) return;

    const target = this.sharedTarget;
    const complete = target && this.shared.handles.every((handle) => wishlist.inList(target.id, handle));

    // Matthias, 06.10.: „Übernehmen“ stays the main button; to the cart via the + on each card (with the size choice)
    this.addAllButton.textContent = this.shared.name ? t('takeOver') : t('takeOverMain');
    this.addAllButton.hidden = Boolean(complete);
  }

  renderEmpty(count) {
    const show = !this.isOverview && count === 0;
    this.empty.hidden = !show;
    if (!show) return;

    const list = this.shared ? null : this.current;
    const id = list ? list.id : null;

    if (this.emptyTitle) this.emptyTitle.textContent = id === TRY ? t('emptyTry') : id === MAIN ? t('emptyMain') : t('emptyList');
    if (this.emptyHint) {
      const hint = id === TRY ? t('emptyTryHint') : id && id !== MAIN ? t('emptyListHint', { list: wishlist.name(list) }) : t('emptyHint');
      this.emptyHint.innerHTML = hint.split('\n').map(escapeHtml).join('<br>');
      this.emptyHint.hidden = !hint;
    }
  }

  renderNote = () => {
    if (!this.note) return;

    let html = '';

    if (!this.shared) {
      if (config.customer && sync.status === 'ready') {
        html = escapeHtml(t('noteAccount'));
      } else if (config.customer && sync.status === 'busy') {
        html = escapeHtml(t('noteBusy'));
      } else if (config.customer && sync.status === 'error') {
        html = escapeHtml(t('noteError'));
      } else {
        html = escapeHtml(t('noteLocal'));

        if (!config.customer && this.loginPossible && config.loginUrl) {
          html += ` ${escapeHtml(t('noteLogin'))} <a href="${escapeHtml(config.loginUrl)}" class="link">${escapeHtml(t('login'))}</a>`;
        }
      }
    }

    this.note.innerHTML = html;
    this.note.hidden = !html || (wishlist.total() === 0 && !wishlist.hasMultiple());
  };

  // Symbole statt „···“-Menü und statt der Textlinks (Matthias, 06.10.): Warenkorb, Teilen, Umbenennen, Löschen; nur, was
  // zur Liste passt (Wunschliste ohne Löschen, „Vor Ort probieren“ ohne Warenkorb und Umbenennen, leere Listen ohne Warenkorb
  // und Teilen). Löschen geht mit einem Tipp und lässt sich in der Meldung rückgängig machen. In der geöffneten Liste ohne
  // Warenkorb-Symbol, dort steht der Knopf „Alle in den Warenkorb“.
  toolsHtml(list, withCart = true) {
    const name = wishlist.name(list);
    const count = list.items.length;
    const tryList = list.id === TRY;
    const tools = [];
    if (withCart && count > 0 && !tryList) tools.push(['cart', CART_TOOL, t('toolCart', { list: name })]);
    if (count > 0) tools.push(['share', SHARE_TOOL, t('toolShare', { list: name })]);
    if (!tryList) tools.push(['rename', RENAME_TOOL, t('toolRename', { list: name })]);
    if (tryList && config.stores.length > 1) tools.push(['store', STORE_TOOL, t('changeStore')]);
    if (list.id !== MAIN) tools.push(['delete', DELETE_TOOL, t('toolDelete', { list: name })]);

    return tools.map(([action, icon, label]) => `<button type="button" class="duk-wl-tile__tool" data-tile-action="${action}" data-list-id="${escapeHtml(list.id)}" aria-label="${escapeHtml(label)}" title="${escapeHtml(label)}">${icon}</button>`).join('');
  }

  // Overview: one tile per list (surface, name, line below, symbols), then „Neue Liste“. Images follow when the cards are there.
  tileHtml(list, images) {
    const handles = list.items.slice(0, 4).map((item) => item.handle);
    const sources = handles.map((handle) => images.get(handle) || '');
    const name = wishlist.name(list);

    return `
      <div class="duk-wl-tile-wrap">
        <a href="${escapeHtml(listUrl(list.id))}" class="duk-wl-tile" data-open-list="${escapeHtml(list.id)}" aria-label="${escapeHtml(t('openList', { list: name }))}">
          ${surfaceHtml(sources, list.items.length)}
          <span class="duk-wl-tile__text">
            <span class="duk-wl-tile__name">${escapeHtml(name)}</span>
            <span class="duk-wl-tile__meta">${escapeHtml(this.listMeta(list))}</span>
          </span>
        </a>
        <div class="duk-wl-tile__tools">${this.toolsHtml(list)}</div>
      </div>`;
  }

  newTileHtml() {
    return `
      <button type="button" class="duk-wl-tile duk-wl-tile--new" data-new-list>
        <span class="duk-wl-surface duk-wl-surface--new" aria-hidden="true">${PLUS_ICON}</span>
        <span class="duk-wl-tile__text">
          <span class="duk-wl-tile__name">${escapeHtml(t('newList'))}</span>
          <span class="duk-wl-tile__meta">${escapeHtml(t('newListHint'))}</span>
        </span>
      </button>`;
  }

  async renderOverview(token) {
    const lists = wishlist.lists;
    const paint = (images) => {
      this.overview.innerHTML = lists.map((list) => this.tileHtml(list, images)).join('') + (lists.length < LIMITS.lists ? this.newTileHtml() : '');
    };

    paint(this.tileImages || new Map());

    const handles = [...new Set(lists.flatMap((list) => list.items.slice(0, 4).map((item) => item.handle)))];
    if (handles.length === 0) return;

    const results = await fetchCards(handles);
    if (token !== this.renderToken) return;

    const images = new Map(this.tileImages || []);
    results.forEach((result, index) => {
      const src = result && result.html ? cardImage(result.html) : '';
      if (src) images.set(handles[index], src);
    });

    this.tileImages = images;
    paint(images);
  }

  renderChrome() {
    if (!this.shared && !wishlist.list(this.currentId)) this.currentId = MAIN;

    const overview = this.isOverview;

    this.renderHead();
    this.renderStore();
    this.renderShared();
    this.renderEmpty(this.handles.length);
    this.renderNote();

    if (this.overview) this.overview.hidden = !overview;
    if (this.list) this.list.hidden = overview;

    if (this.list) {
      if (this.shared) {
        this.list.removeAttribute('data-duk-wishlist-context');
      } else {
        this.list.setAttribute('data-duk-wishlist-context', this.current.id);
      }
    }
  }

  async render() {
    const token = ++this.renderToken;

    this.renderChrome();

    if (this.isOverview) {
      this.list.replaceChildren();
      if (this.status) this.status.hidden = true;
      await this.renderOverview(token);
      return;
    }

    const handles = this.handles;
    const listId = this.shared ? null : this.current.id;

    if (handles.length === 0) {
      this.list.replaceChildren();
      if (this.status) this.status.hidden = true;
      return;
    }

    if (this.status) {
      this.status.textContent = this.status.getAttribute('data-loading') || this.status.textContent;
      this.status.hidden = false;
    }

    const results = await fetchCards(handles);

    if (token !== this.renderToken) return;

    const fragment = document.createDocumentFragment();
    const missing = [];
    let failed = 0;

    results.forEach((result, index) => {
      const handle = handles[index];

      if (result && result.html) {
        const card = cardFromHtml(result.html);

        if (card) {
          const cardHandle = card.getAttribute('handle');

          // The product got a new handle (Shopify redirected): keep following it
          if (!this.shared && cardHandle && cardHandle !== handle) {
            wishlist.update(handle, { handle: cardHandle });
          }

          fragment.append(card);
          return;
        }
      }

      if (result && result.missing) {
        missing.push(handle);
      } else {
        failed += 1;
      }
    });

    this.list.replaceChildren(fragment);
    if (this.status) this.status.hidden = true;

    // Products the shop does not show right now (draft for the season, not sold in this market) stay in the list and
    // simply have no card; they come back by themselves (1.2.4, Fehlerliste Nr. 5: before, they were removed for good)

    if (failed > 0 && this.list.children.length === 0 && this.status) {
      this.status.textContent = t('error');
      this.status.hidden = false;
    }
  }

  onChange = (event) => {
    if (this.ignoreChanges) return;

    const detail = event.detail || {};

    if (this.shared) {
      this.renderChrome();
      return;
    }

    // The lists arrived from the account or another tab: open the right view, unless one was chosen
    if (detail.external && !this.pinned) {
      this.view = this.autoView();
      this.currentId = this.defaultId();
    }

    // A deleted list came back (undo): show it again; in the overview the tile simply comes back
    if (detail.restored) {
      if (this.isOverview) this.render();
      else this.select(detail.listId, { replace: true, keepScroll: true });
      return;
    }

    // Overview: tiles and counts follow every change
    if (this.isOverview) {
      this.render();
      return;
    }

    // Removed from the list on screen: take the card out instead of reloading everything
    if (detail.handle && detail.added === false && detail.listId === this.current.id) {
      this.list.querySelectorAll(CARD_SELECTOR).forEach((card) => {
        if (card.getAttribute('handle') !== detail.handle) return;

        card.classList.add('is-removing');
        window.setTimeout(() => card.remove(), 250);
      });

      this.renderChrome();
      return;
    }

    // Other lists, names, stores: only the frame changes
    if (!detail.external && detail.listId !== this.current.id) {
      this.renderChrome();
      return;
    }

    if (detail.renamed || detail.store) {
      this.renderChrome();
      return;
    }

    this.render();
  };

  onClick = async (event) => {
    // Tiles are links (open in a new tab works); a plain tap opens the list without reloading the page
    const tile = event.target.closest('[data-open-list]');
    if (tile && this.contains(tile)) {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button > 0) return;
      event.preventDefault();
      this.select(tile.getAttribute('data-open-list'));
      return;
    }

    const back = event.target.closest('[data-duk-wishlist-back]');
    if (back && this.contains(back)) {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button > 0) return;
      event.preventDefault();
      this.showOverview();
      return;
    }

    const target = event.target.closest('button');
    if (!target || !this.contains(target) || target.closest('product-card')) return;

    if (target.hasAttribute('data-tile-action')) {
      const list = wishlist.list(target.getAttribute('data-list-id'));
      const action = target.getAttribute('data-tile-action');
      if (!list) return;

      if (action === 'cart') this.addListToCart(target, list);
      if (action === 'share') await this.share(list);
      if (action === 'rename') listMenu.open(this, list.id, 'rename');
      if (action === 'store') picker.open({ mode: 'store', listId: list.id });
      if (action === 'delete') {
        const open = !this.isOverview && list.id === this.current.id;
        const deleted = wishlist.deleteList(list.id);
        if (deleted) deletedToast(deleted);

        // Aus der geöffneten Liste: zur Übersicht, wenn es noch mehr als eine Liste gibt, sonst zur verbliebenen Liste
        if (deleted && open) {
          this.currentId = this.defaultId();
          if (this.autoView() === 'overview') this.showOverview({ replace: true });
          else this.select(this.currentId, { replace: true });
        }
      }
      return;
    }

    // Neue Liste aus der Übersicht: danach wieder die Übersicht mit der neuen Kachel, nicht die leere Liste (1.2.5,
    // Matthias 08.10.)
    if (target.hasAttribute('data-new-list')) {
      picker.open({
        mode: 'create',
        onCreate: (list) => {
          this.showOverview({ replace: true, keepScroll: true });
          window.requestAnimationFrame(() => {
            const tile = [...this.querySelectorAll('[data-open-list]')].find((element) => element.getAttribute('data-open-list') === list.id);
            if (!tile) return;
            tile.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            tile.focus({ preventScroll: true });
          });
        }
      });
      return;
    }

    if (target.hasAttribute('data-share')) {
      await this.share();
      return;
    }

    if (target.hasAttribute('data-rename')) {
      this.panelMode = this.panelMode === 'rename' ? null : 'rename';
      this.renderPanel();
      this.panel.querySelector('input')?.focus();
      return;
    }

    if (target.hasAttribute('data-delete')) {
      this.panelMode = this.panelMode === 'delete' ? null : 'delete';
      this.renderPanel();
      return;
    }

    if (target.hasAttribute('data-panel-cancel')) {
      this.panelMode = null;
      this.renderPanel();
      return;
    }

    if (target.hasAttribute('data-delete-confirm')) {
      this.panelMode = null;
      const deleted = wishlist.deleteList(this.current.id);
      if (deleted) deletedToast(deleted);
      this.currentId = this.defaultId();
      // After deleting: the overview when there is more than one list left, else the remaining list
      if (wishlist.lists.length > 1) {
        this.showOverview({ replace: true });
      } else {
        this.select(this.currentId, { replace: true });
      }
      return;
    }

    if (target.hasAttribute('data-store-pick')) {
      picker.open({ mode: 'store', listId: this.current.id });
      return;
    }

    if (target.hasAttribute('data-duk-wishlist-add-all')) {
      this.takeOver();
      return;
    }

    if (target.hasAttribute('data-add-list')) {
      this.addListToCart(target);
    }
  };

  // Alle verfügbaren Produkte der Liste in den Warenkorb, je eines; bei mehreren Größen die erste verfügbare (der
  // „ab“-Preis der Karte). Was schon im Warenkorb liegt, bleibt einmal drin. Danach geht der Warenkorb auf.
  async addListToCart(button, list = this.current) {
    if (!list || (button && button.disabled)) return;

    // Das Symbol unter einer Kachel bleibt ein Symbol (nur blasser), der Knopf in der Liste zeigt „Wird hinzugefügt …“
    const iconOnly = Boolean(button && button.classList.contains('duk-wl-tile__tool'));
    const label = button && !iconOnly ? button.textContent : '';
    if (button) {
      button.disabled = true;
      if (iconOnly) button.setAttribute('aria-busy', 'true');
      else button.textContent = t('addingAll');
    }

    try {
      const cart = await fetch(`${routesRoot()}cart.js`, { headers: { Accept: 'application/json' } }).then((response) => response.json());
      const inCart = new Set((cart.items || []).map((item) => String(item.product_id)));
      const products = await Promise.all(list.items.map((item) =>
        fetch(`${routesRoot()}products/${encodeURIComponent(item.handle)}.js`, { headers: { Accept: 'application/json' } })
          .then((response) => (response.ok ? response.json() : null))
          .catch(() => null)
      ));

      const items = products
        .filter((product) => product && !inCart.has(String(product.id)))
        .map((product) => (product.variants || []).find((variant) => variant.available))
        .filter(Boolean)
        .map((variant) => ({ id: variant.id, quantity: 1 }));

      if (items.length === 0) {
        toast(escapeHtml(t('addAllNone')), 4000);
        return;
      }

      const add = await fetch(`${routesRoot()}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items })
      });
      if (!add.ok) throw new Error('cart/add');

      const skipped = list.items.length - items.length;
      const done = items.length === 1 ? t('addedAllOne') : t('addedAllMany', { n: items.length });
      toast(escapeHtml(skipped > 0 ? `${done} ${t('addedAllSkipped', { n: skipped })}` : done), 5000);
      await refreshCartDrawer();
    } catch (error) {
      toast(escapeHtml(t('addAllError')), 4000);
    } finally {
      if (button) {
        button.disabled = false;
        if (iconOnly) button.removeAttribute('aria-busy');
        else button.textContent = label;
      }
    }
  }

  onSubmit = (event) => {
    if (!event.target.matches('[data-rename-form]')) return;

    event.preventDefault();
    const name = cleanName(event.target.querySelector('input').value);
    this.panelMode = null;

    // The main list keeps its default name when the field is emptied
    if (name || this.current.id === MAIN) {
      wishlist.renameList(this.current.id, this.current.id === MAIN && name === t('mainList') ? '' : name);
    }

    this.renderChrome();
  };

  async share(list = this.current) {
    const params = new URLSearchParams();
    params.set(SHARE_PARAM, list.items.slice(0, MAX_SHARED).map((item) => item.handle).join(','));
    if (list.name || list.id === TRY) params.set(SHARE_NAME_PARAM, wishlist.name(list));
    if (list.store) params.set(SHARE_STORE_PARAM, list.store);

    const url = `${window.location.origin}${config.pageUrl}?${params.toString().replace(/%2C/g, ',')}`;
    const title = t('shareTitle', { list: wishlist.name(list) });

    if (navigator.share) {
      try {
        // Name auch als Text: WhatsApp & Co. zeigen den Titel nicht (1.2.4)
        await navigator.share({ title, text: title, url });
        return;
      } catch (error) {
        if (error && error.name === 'AbortError') return;
      }
    }

    try {
      await navigator.clipboard.writeText(url);
      toast(escapeHtml(t('copied')));
    } catch (error) {
      window.prompt(title, url);
    }
  }

  takeOver() {
    if (!this.shared) return;

    let target = this.sharedTarget;
    if (!target) target = wishlist.createList(this.shared.name, this.shared.store);
    if (!target) return;

    let added = 0;

    // Oldest first, so the shared order stays on top
    [...this.shared.handles].reverse().forEach((handle) => {
      const list = wishlist.add(handle, target.id);
      if (list) {
        added += 1;
        afterAdd(handle, list, 'share');
      }
    });

    if (added > 0) {
      const name = wishlist.name(target);
      const main = target.id === MAIN;
      const text = added === 1
        ? (main ? t('addedToastMain') : t('addedToast', { list: name }))
        : (main ? t('addedManyToastMain', { n: added }) : t('addedManyToast', { n: added, list: name }));
      toast(`${escapeHtml(text)} <a href="${escapeHtml(listUrl(target.id))}" class="link">${escapeHtml(t('view'))}</a>`, 5000);
    }

    this.renderChrome();
  }
}

if (!window.customElements.get('duk-wishlist-page')) {
  window.customElements.define('duk-wishlist-page', DukWishlistPage);
}

/* ---------------------------------------------------------------------------------------------- */
/* Start the account sync once everything above is ready                                           */
/* ---------------------------------------------------------------------------------------------- */

sync.init();
